import { useState, useEffect } from 'react'
import { Plus, X, Package } from 'lucide-react'
import ProductStorefrontV3Section from './ProductStorefrontV3Section'

export function ProductForm({
  product,
  feeds,
  selectedFeedId,
  onFeedChange,
  onSubmit,
  onCancel,
  isLoading,
  isMobile = false,
  asModal = true,
  availableProducts = [],
}: {
  product?: any
  feeds?: any[]
  selectedFeedId?: number | null
  onFeedChange?: (feedId: number | null) => void
  onSubmit: (data: any, targetFeedId?: number) => void
  onCancel: () => void
  isLoading: boolean
  isMobile?: boolean
  asModal?: boolean
  availableProducts?: any[]
}) {
  const [localSelectedFeedId, setLocalSelectedFeedId] = useState<number | null>(selectedFeedId || null)
  const [formData, setFormData] = useState(() => {
    if (product) {
      // Get media from either top-level (API response) or content.media (legacy)
      const mediaData = product.media || product.content?.media || [];
      
      return {
        product_id: product.product_id || '',
        external_product_id: product.product_id_external || product.external_product_id || '',
        parent_id: product.parent_id || '',
        status: product.status || 'active',
        identifiers: product.identifiers || {},
        content: {
          title: product.content?.title || '',
          subtitle: product.content?.subtitle || '',
          description: product.content?.description || '',
          bullet_points: product.content?.bullet_points || [],
          tags: product.content?.tags || [],
          media: mediaData,
          localized_content: product.content?.localized_content || [],
        },
        taxonomy: {
          category_path: product.taxonomy?.category_path || '',
          category_code: product.taxonomy?.category_code || '',
          brand: product.taxonomy?.brand || '',
          attributes: product.taxonomy?.attributes || [],
          target_audience: product.taxonomy?.target_audience || {},
        },
        compliance: {
          warnings: product.compliance?.warnings || [],
          age_restriction: product.compliance?.age_restriction || undefined,
          regulated: product.compliance?.regulated || false,
          regulatory_details: product.compliance?.regulatory_details || '',
        },
        lifecycle: {
          release_date: product.lifecycle?.release_date || '',
          discontinue_date: product.lifecycle?.discontinue_date || '',
          seasonality: product.lifecycle?.seasonality || '',
        },
        offers: product.offers?.map((offer: any) => {
          // Handle availability - could be a string (from sync) or an object
          let availabilityObj = {
            status: 'in_stock',
            stock_quantity: undefined as number | undefined,
            lead_time_days: undefined as number | undefined,
            restock_date: '',
          };
          
          if (typeof offer.availability === 'string') {
            // Availability is stored as just a status string
            availabilityObj.status = offer.availability;
          } else if (offer.availability) {
            // Availability is an object
            availabilityObj = {
              status: offer.availability.status || 'in_stock',
              stock_quantity: offer.availability.stock_quantity,
              lead_time_days: offer.availability.lead_time_days,
              restock_date: offer.availability.restock_date || '',
            };
          }
          
          return {
            offer_id: offer.offer_id || offer.offer_id_external || '',
            merchant_listing_id: offer.merchant_listing_id || '',
            currency: offer.currency || 'USD',
            price: offer.price || 0,
            list_price: offer.list_price || undefined,
            sale: {
              sale_price: offer.sale?.sale_price || undefined,
              start_time: offer.sale?.start_time || '',
              end_time: offer.sale?.end_time || '',
            },
            tax: {
              tax_included: offer.tax?.tax_included || false,
              tax_rate: offer.tax?.tax_rate || undefined,
              tax_region: offer.tax?.tax_region || '',
            },
            availability: availabilityObj,
            fulfillment: {
              shipping_options: offer.fulfillment?.shipping_options || [],
              pickup: {
                enabled: offer.fulfillment?.pickup?.enabled || false,
                locations: offer.fulfillment?.pickup?.locations || [],
              },
            },
            geographic_overrides: offer.geographic_overrides || [],
          };
        }) || [{
          offer_id: '',
          merchant_listing_id: '',
          currency: 'USD',
          price: 0,
          list_price: undefined,
          sale: {
            sale_price: undefined,
            start_time: '',
            end_time: '',
          },
          tax: {
            tax_included: false,
            tax_rate: undefined,
            tax_region: '',
          },
          availability: {
            status: 'in_stock',
            stock_quantity: undefined,
            lead_time_days: undefined,
            restock_date: '',
          },
          fulfillment: {
            shipping_options: [],
            pickup: {
              enabled: false,
              locations: [],
            },
          },
          geographic_overrides: [],
        }],
        related_products: product.related_products || [],
        performance: {
          popularity_score: product.performance?.popularity_score || undefined,
          review_count: product.performance?.review_count || undefined,
          average_rating: product.performance?.average_rating || undefined,
          return_rate: product.performance?.return_rate || undefined,
        },
        extensions: product.extensions || {},
        payments: product.payments || {
          accepted_methods: [],
          instant_checkout_enabled: false,
        },
      }
    }

    // Auto-generate parent_id (UUID-based)
    const generateParentId = () => {
      return 'parent_' + crypto.randomUUID().replace(/-/g, '').substring(0, 16)
    }

    // Check for external product ID from integration (stored in sessionStorage)
    // Only populate if it exists from external integration, don't auto-generate
    const externalProductId = sessionStorage.getItem('pendingExternalProductId') || null
    if (externalProductId) {
      sessionStorage.removeItem('pendingExternalProductId')
    }

    return {
      product_id: '', // Will be auto-generated by database
      external_product_id: externalProductId, // Only populated from external integration if available, null otherwise
      parent_id: generateParentId(), // Auto-generated
      status: 'inactive', // Default to inactive on create
      identifiers: {},
      content: {
        title: '',
        subtitle: '',
        description: '',
        bullet_points: [],
        tags: [],
        media: [],
        localized_content: [],
      },
      taxonomy: {
        category_path: '',
        category_code: '',
        brand: '',
        attributes: [],
        target_audience: {},
      },
      compliance: {
        warnings: [],
        age_restriction: undefined,
        regulated: false,
        regulatory_details: '',
      },
      lifecycle: {
        release_date: '',
        discontinue_date: '',
        seasonality: '',
      },
      offers: [{
        offer_id: 'offer_' + crypto.randomUUID().replace(/-/g, '').substring(0, 16), // Auto-generated
        merchant_listing_id: 'listing_' + crypto.randomUUID().replace(/-/g, '').substring(0, 16), // Auto-generated
        currency: 'USD',
        price: 0,
        list_price: undefined,
        sale: {
          sale_price: undefined,
          start_time: '',
          end_time: '',
        },
        tax: {
          tax_included: false,
          tax_rate: undefined,
          tax_region: '',
        },
        availability: {
          status: 'in_stock',
          stock_quantity: undefined,
          lead_time_days: undefined,
          restock_date: '',
        },
      }],
      related_products: [],
      performance: {
        popularity_score: undefined,
        review_count: undefined,
        average_rating: undefined,
        return_rate: undefined,
      },
      extensions: {},
      payments: {
        accepted_methods: [],
        instant_checkout_enabled: false,
      },
    }
  })

  const [bulletPoint, setBulletPoint] = useState('')
  const [tag, setTag] = useState('')
  const [warning, setWarning] = useState('')
  const [localizedBulletPoints, setLocalizedBulletPoints] = useState<{ [key: number]: string }>({})
  const [attributeName, setAttributeName] = useState('')
  const [attributeValue, setAttributeValue] = useState('')
  const [attributeUnit, setAttributeUnit] = useState('')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const feedId = localSelectedFeedId || selectedFeedId
    onSubmit(formData, feedId || undefined)
  }

  // Update form data when product prop changes (e.g., when API data loads)
  useEffect(() => {
    if (product) {
      // Get media from either top-level (API response) or content.media (legacy)
      const mediaData = product.media || product.content?.media || [];
      
      // Process offers with proper availability handling
      const processedOffers = product.offers?.map((offer: any) => {
        let availabilityObj = {
          status: 'in_stock',
          stock_quantity: undefined as number | undefined,
          lead_time_days: undefined as number | undefined,
          restock_date: '',
        };
        
        if (typeof offer.availability === 'string') {
          availabilityObj.status = offer.availability;
        } else if (offer.availability) {
          availabilityObj = {
            status: offer.availability.status || 'in_stock',
            stock_quantity: offer.availability.stock_quantity,
            lead_time_days: offer.availability.lead_time_days,
            restock_date: offer.availability.restock_date || '',
          };
        }
        
        return {
          offer_id: offer.offer_id || offer.offer_id_external || '',
          merchant_listing_id: offer.merchant_listing_id || '',
          currency: offer.currency || 'USD',
          price: offer.price || 0,
          list_price: offer.list_price || undefined,
          sale: {
            sale_price: offer.sale?.sale_price || undefined,
            start_time: offer.sale?.start_time || '',
            end_time: offer.sale?.end_time || '',
          },
          tax: {
            tax_included: offer.tax?.tax_included || false,
            tax_rate: offer.tax?.tax_rate || undefined,
            tax_region: offer.tax?.tax_region || '',
          },
          availability: availabilityObj,
          fulfillment: {
            shipping_options: offer.fulfillment?.shipping_options || [],
            pickup: {
              enabled: offer.fulfillment?.pickup?.enabled || false,
              locations: offer.fulfillment?.pickup?.locations || [],
            },
          },
          geographic_overrides: offer.geographic_overrides || [],
        };
      });

      setFormData(prev => ({
        ...prev,
        product_id: product.product_id || prev.product_id,
        external_product_id: product.product_id_external || product.external_product_id || prev.external_product_id,
        parent_id: product.parent_id || prev.parent_id,
        status: product.status || prev.status,
        identifiers: product.identifiers || prev.identifiers,
        content: {
          title: product.content?.title || prev.content.title,
          subtitle: product.content?.subtitle || prev.content.subtitle,
          description: product.content?.description || prev.content.description,
          bullet_points: product.content?.bullet_points || prev.content.bullet_points,
          tags: product.content?.tags || prev.content.tags,
          media: mediaData.length > 0 ? mediaData : prev.content.media,
          localized_content: product.content?.localized_content || prev.content.localized_content,
        },
        taxonomy: {
          category_path: product.taxonomy?.category_path || prev.taxonomy.category_path,
          category_code: product.taxonomy?.category_code || prev.taxonomy.category_code,
          brand: product.taxonomy?.brand || prev.taxonomy.brand,
          attributes: product.taxonomy?.attributes || product.attributes || prev.taxonomy.attributes,
          target_audience: product.taxonomy?.target_audience || prev.taxonomy.target_audience,
        },
        offers: processedOffers && processedOffers.length > 0 ? processedOffers : prev.offers,
        performance: product.performance || prev.performance,
        extensions: product.extensions || prev.extensions,
      }));
    }
  }, [product]);

  // Update local state when prop changes
  useEffect(() => {
    if (selectedFeedId !== undefined) {
      setLocalSelectedFeedId(selectedFeedId)
    }
  }, [selectedFeedId])

  const addBulletPoint = () => {
    if (bulletPoint.trim()) {
      setFormData(prev => ({
        ...prev,
        content: {
          ...prev.content,
          bullet_points: [...(prev.content.bullet_points || []), bulletPoint.trim()]
        }
      }))
      setBulletPoint('')
    }
  }

  const removeBulletPoint = (index: number) => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        bullet_points: prev.content.bullet_points.filter((_: any, i: number) => i !== index)
      }
    }))
  }

  const addTag = () => {
    if (tag.trim()) {
      setFormData(prev => ({
        ...prev,
        content: {
          ...prev.content,
          tags: [...(prev.content.tags || []), tag.trim()]
        }
      }))
      setTag('')
    }
  }

  const removeTag = (index: number) => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        tags: prev.content.tags.filter((_: any, i: number) => i !== index)
      }
    }))
  }

  const addWarning = () => {
    if (warning.trim()) {
      setFormData(prev => ({
        ...prev,
        compliance: {
          ...prev.compliance,
          warnings: [...(prev.compliance?.warnings || []), warning.trim()]
        }
      }))
      setWarning('')
    }
  }

  const removeWarning = (index: number) => {
    setFormData(prev => ({
      ...prev,
      compliance: {
        ...prev.compliance,
        warnings: (prev.compliance?.warnings || []).filter((_: any, i: number) => i !== index)
      }
    }))
  }

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    Array.from(files).forEach((file) => {
      const isImage = file.type.startsWith('image/')
      const isVideo = file.type.startsWith('video/')

      if (!isImage && !isVideo) {
        alert(`${file.name} is not a supported media file (image or video)`)
        return
      }

      const reader = new FileReader()
      reader.onload = (event) => {
        const mediaUrl = event.target?.result as string

        if (isImage) {
          const img = new Image()
          img.onload = () => {
            setFormData(prev => {
              const currentMedia = prev.content.media || []
              const isFirstMedia = currentMedia.length === 0

              return {
                ...prev,
                content: {
                  ...prev.content,
                  media: [...currentMedia, {
                    type: 'image',
                    url: mediaUrl,
                    is_primary: isFirstMedia,
                    width: img.width,
                    height: img.height,
                    mime_type: file.type,
                    caption: '',
                    variants: []
                  }]
                }
              }
            })
          }
          img.src = mediaUrl
        } else if (isVideo) {
          const video = document.createElement('video')
          video.preload = 'metadata'
          video.onloadedmetadata = () => {
            setFormData(prev => {
              const currentMedia = prev.content.media || []
              const isFirstMedia = currentMedia.length === 0

              return {
                ...prev,
                content: {
                  ...prev.content,
                  media: [...currentMedia, {
                    type: 'video',
                    url: mediaUrl,
                    is_primary: isFirstMedia,
                    width: video.videoWidth,
                    height: video.videoHeight,
                    mime_type: file.type,
                    caption: '',
                    variants: []
                  }]
                }
              }
            })
          }
          video.src = mediaUrl
        }
      }
      reader.readAsDataURL(file)
    })

    // Reset input
    e.target.value = ''
  }

  const addLocalizedContent = () => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        localized_content: [...(prev.content.localized_content || []), {
          locale: '',
          title: '',
          subtitle: '',
          description: '',
          bullet_points: []
        }]
      }
    }))
  }

  const removeLocalizedContent = (index: number) => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        localized_content: prev.content.localized_content.filter((_: any, i: number) => i !== index)
      }
    }))
    // Clean up localized bullet points state
    const newLocalizedBulletPoints = { ...localizedBulletPoints }
    delete newLocalizedBulletPoints[index]
    setLocalizedBulletPoints(newLocalizedBulletPoints)
  }

  const updateLocalizedContent = (index: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        localized_content: (prev.content.localized_content || []).map((item: any, i: number) =>
          i === index ? { ...item, [field]: value } : item
        )
      }
    }))
  }

  const addLocalizedBulletPoint = (localizedIndex: number) => {
    const bulletPoint = localizedBulletPoints[localizedIndex]
    if (bulletPoint && bulletPoint.trim()) {
      setFormData(prev => ({
        ...prev,
        content: {
          ...prev.content,
          localized_content: (prev.content.localized_content || []).map((item: any, i: number) =>
            i === localizedIndex
              ? { ...item, bullet_points: [...(item.bullet_points || []), bulletPoint.trim()] }
              : item
          )
        }
      }))
      setLocalizedBulletPoints({ ...localizedBulletPoints, [localizedIndex]: '' })
    }
  }

  const removeLocalizedBulletPoint = (localizedIndex: number, bulletIndex: number) => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        localized_content: (prev.content.localized_content || []).map((item: any, i: number) =>
          i === localizedIndex
            ? { ...item, bullet_points: (item.bullet_points || []).filter((_: any, bi: number) => bi !== bulletIndex) }
            : item
        )
      }
    }))
  }

  const addAttribute = () => {
    if (attributeName.trim() && attributeValue.trim()) {
      setFormData(prev => ({
        ...prev,
        taxonomy: {
          ...prev.taxonomy,
          attributes: [...(prev.taxonomy.attributes || []), {
            name: attributeName.trim(),
            value: attributeValue.trim(),
            unit: attributeUnit.trim() || undefined
          }]
        }
      }))
      setAttributeName('')
      setAttributeValue('')
      setAttributeUnit('')
    }
  }

  const removeAttribute = (index: number) => {
    setFormData(prev => ({
      ...prev,
      taxonomy: {
        ...prev.taxonomy,
        attributes: prev.taxonomy.attributes.filter((_: any, i: number) => i !== index)
      }
    }))
  }

  const removeMedia = (index: number) => {
    setFormData(prev => {
      const updatedMedia = prev.content.media.filter((_: any, i: number) => i !== index)
      // If we removed the primary image and there are still images, make the first one primary
      if (prev.content.media[index]?.is_primary && updatedMedia.length > 0) {
        updatedMedia[0].is_primary = true
      }
      return {
        ...prev,
        content: {
          ...prev.content,
          media: updatedMedia
        }
      }
    })
  }

  const setPrimaryImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        media: prev.content.media.map((media: any, i: number) => ({
          ...media,
          is_primary: i === index
        }))
      }
    }))
  }

  const updateMediaField = (index: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        media: prev.content.media.map((media: any, i: number) =>
          i === index ? { ...media, [field]: value } : media
        )
      }
    }))
  }

  const removeVariant = (mediaIndex: number, variantIndex: number) => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        media: prev.content.media.map((media: any, i: number) =>
          i === mediaIndex
            ? {
              ...media,
              variants: media.variants.filter((_: any, vi: number) => vi !== variantIndex)
            }
            : media
        )
      }
    }))
  }

  const updateVariantField = (mediaIndex: number, variantIndex: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        media: prev.content.media.map((media: any, i: number) =>
          i === mediaIndex
            ? {
              ...media,
              variants: media.variants.map((variant: any, vi: number) =>
                vi === variantIndex ? { ...variant, [field]: value } : variant
              )
            }
            : media
        )
      }
    }))
  }

  const handleVariantUpload = (mediaIndex: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    Array.from(files).forEach((file) => {
      if (!file.type.startsWith('image/')) {
        alert(`${file.name} is not an image file`)
        return
      }

      const reader = new FileReader()
      reader.onload = (event) => {
        const imageUrl = event.target?.result as string
        const img = new Image()
        img.onload = () => {
          setFormData(prev => ({
            ...prev,
            content: {
              ...prev.content,
              media: prev.content.media.map((media: any, i: number) =>
                i === mediaIndex
                  ? {
                    ...media,
                    variants: [...(media.variants || []), {
                      url: imageUrl,
                      width: img.width,
                      height: img.height,
                      mime_type: file.type
                    }]
                  }
                  : media
              )
            }
          }))
        }
        img.src = imageUrl
      }
      reader.readAsDataURL(file)
    })

    e.target.value = ''
  }

  const formContent = (
    <>
      <form id="product-form" onSubmit={handleSubmit}>
        {/* Section 1: Basic Information */}
        <div style={{
          marginBottom: '2rem',
          padding: '1.5rem',
          backgroundColor: 'var(--color-gray-50)',
          borderRadius: '12px',
          border: '1px solid var(--color-gray-200)'
        }}>
          <h3 style={{
            marginBottom: '1.5rem',
            fontSize: '1.125rem',
            fontWeight: 600,
            color: 'var(--color-gray-900)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <div style={{
              width: '4px',
              height: '20px',
              backgroundColor: 'var(--color-sky-blue)',
              borderRadius: '2px'
            }} />
            Basic Information
          </h3>

          {/* Feed Selection (only when adding, not editing) */}
          {!product && feeds && feeds.length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                Select Feed *
              </label>
              <select
                value={localSelectedFeedId || ''}
                onChange={(e) => {
                  const feedId = e.target.value ? parseInt(e.target.value) : null
                  setLocalSelectedFeedId(feedId)
                  if (onFeedChange) {
                    onFeedChange(feedId)
                  }
                }}
                className="input"
                required={!product}
                style={{ width: '100%' }}
              >
                <option value="">-- Select a feed --</option>
                {feeds.map((feed: any) => (
                  <option key={feed.feed_id || feed.feedId} value={feed.feed_id || feed.feedId}>
                    {feed.merchant?.name || `Feed #${feed.feed_id || feed.feedId}`} {feed.status ? `(${feed.status})` : ''}
                  </option>
                ))}
              </select>
              <p style={{ fontSize: '0.75rem', color: 'var(--color-gray-600)', marginTop: '0.5rem' }}>
                Select the feed to add this experience to
              </p>
            </div>
          )}

          {/* Basic Information - Only show when editing */}
          {product && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                    External Experience ID
                  </label>
                  <input
                    type="text"
                    value={formData.external_product_id || ''}
                    onChange={(e) => setFormData({ ...formData, external_product_id: e.target.value.trim() || null })}
                    className="input"
                    maxLength={100}
                    placeholder="From external integration (optional)"
                  />
                  <p style={{ fontSize: '0.75rem', color: 'var(--color-gray-600)', marginTop: '0.25rem' }}>
                    Optional: External identifier from integration. Leave empty if not available.
                  </p>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                    Status *
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="input"
                    required
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="discontinued">Discontinued</option>
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                  Parent ID (for variants)
                </label>
                <input
                  type="text"
                  value={formData.parent_id}
                  onChange={(e) => setFormData({ ...formData, parent_id: e.target.value })}
                  className="input"
                />
              </div>
            </>
          )}
        </div>

        {/* Section 2: Experience Details */}
        <div style={{
          marginBottom: '2rem',
          padding: '1.5rem',
          backgroundColor: 'var(--color-gray-50)',
          borderRadius: '12px',
          border: '1px solid var(--color-gray-200)'
        }}>
          <h3 style={{
            marginBottom: '1.5rem',
            fontSize: '1.125rem',
            fontWeight: 600,
            color: 'var(--color-gray-900)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <div style={{
              width: '4px',
              height: '20px',
              backgroundColor: 'var(--color-sky-blue)',
              borderRadius: '2px'
            }} />
            Experience Details
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                Title *
              </label>
              <input
                type="text"
                value={formData.content.title}
                onChange={(e) => setFormData({
                  ...formData,
                  content: { ...formData.content, title: e.target.value }
                })}
                className="input"
                required
                maxLength={200}
                style={{ width: '100%' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                Subtitle
              </label>
              <input
                type="text"
                value={formData.content.subtitle}
                onChange={(e) => setFormData({
                  ...formData,
                  content: { ...formData.content, subtitle: e.target.value }
                })}
                className="input"
                style={{ width: '100%' }}
              />
            </div>
          </div>

          <div style={{ marginBottom: '1rem', width: '100%' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
              Description *
            </label>
            <textarea
              value={formData.content.description}
              onChange={(e) => setFormData({
                ...formData,
                content: { ...formData.content, description: e.target.value }
              })}
              className="input"
              required
              rows={4}
              maxLength={5000}
              style={{ width: '100%' }}
            />
          </div>

          {/* Bullet Points */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
              Bullet Points
            </label>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <input
                type="text"
                value={bulletPoint}
                onChange={(e) => setBulletPoint(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addBulletPoint()
                  }
                }}
                className="input"
                placeholder="Add bullet point..."
                style={{ flex: 1 }}
              />
              <button
                type="button"
                onClick={addBulletPoint}
                className="button button-secondary"
              >
                Add
              </button>
            </div>
            {formData.content.bullet_points && formData.content.bullet_points.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {formData.content.bullet_points.map((point: string, index: number) => (
                  <div key={index} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.5rem',
                    backgroundColor: 'var(--color-gray-50)',
                    borderRadius: '4px'
                  }}>
                    <span style={{ fontSize: '0.875rem' }}>{point}</span>
                    <button
                      type="button"
                      onClick={() => removeBulletPoint(index)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#dc2626',
                        cursor: 'pointer',
                        padding: '0.25rem'
                      }}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Tags */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
              Tags
            </label>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <input
                type="text"
                value={tag}
                onChange={(e) => setTag(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addTag()
                  }
                }}
                className="input"
                placeholder="Add tag..."
                style={{ flex: 1 }}
              />
              <button
                type="button"
                onClick={addTag}
                className="button button-secondary"
              >
                Add
              </button>
            </div>
            {formData.content.tags && formData.content.tags.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {formData.content.tags.map((tag: string, index: number) => (
                  <span key={index} style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    padding: '0.25rem 0.75rem',
                    backgroundColor: 'var(--color-sky-blue)',
                    color: 'white',
                    borderRadius: '16px',
                    fontSize: '0.75rem'
                  }}>
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(index)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: 'white',
                        cursor: 'pointer',
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

        </div>

        {/* Section 3: Media & Images */}
        <div style={{
          marginBottom: '2rem',
          padding: '1.5rem',
          backgroundColor: 'var(--color-gray-50)',
          borderRadius: '12px',
          border: '1px solid var(--color-gray-200)'
        }}>
          <h3 style={{
            marginBottom: '1.5rem',
            fontSize: '1.125rem',
            fontWeight: 600,
            color: 'var(--color-gray-900)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <div style={{
              width: '4px',
              height: '20px',
              backgroundColor: 'var(--color-sky-blue)',
              borderRadius: '2px'
            }} />
            Media & Images
          </h3>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
              Upload Media
            </label>
            <div style={{ marginBottom: '1rem' }}>
              <input
                type="file"
                accept="image/*,video/*"
                multiple
                onChange={handleImageUpload}
                style={{ display: 'none' }}
                id="media-upload-input"
              />
              <label
                htmlFor="media-upload-input"
                style={{
                  display: 'inline-block',
                  padding: '0.625rem 1rem',
                  borderRadius: '8px',
                  border: '1px solid var(--color-sky-blue)',
                  backgroundColor: 'var(--color-sky-blue)',
                  color: 'white',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
              >
                <Plus size={16} style={{ display: 'inline', marginRight: '0.5rem', verticalAlign: 'middle' }} />
                Upload Media
              </label>
            </div>
            {formData.content.media && formData.content.media.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {formData.content.media.map((media: any, index: number) => (
                  <div
                    key={index}
                    style={{
                      border: media.is_primary ? '2px solid var(--color-sky-blue)' : '1px solid var(--color-gray-300)',
                      borderRadius: '8px',
                      padding: '1rem',
                      backgroundColor: media.is_primary ? 'var(--color-gray-50)' : 'white'
                    }}
                  >
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '200px minmax(0, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
                      {/* Media Preview */}
                      <div style={{ position: 'relative', minWidth: 0 }}>
                        {media.type === 'image' ? (
                          <img
                            src={media.url}
                            alt={`Media ${index + 1}`}
                            style={{
                              width: '100%',
                              height: '200px',
                              objectFit: 'cover',
                              borderRadius: '4px',
                              border: '1px solid var(--color-gray-300)'
                            }}
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none'
                            }}
                          />
                        ) : (
                          <div style={{
                            width: '100%',
                            height: '200px',
                            borderRadius: '4px',
                            border: '1px solid var(--color-gray-300)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'var(--color-gray-100)',
                            color: 'var(--color-gray-600)'
                          }}>
                            {media.type || 'Media'}
                          </div>
                        )}
                        {media.is_primary && (
                          <div style={{
                            position: 'absolute',
                            top: '0.5rem',
                            left: '0.5rem',
                            background: 'var(--color-sky-blue)',
                            color: 'white',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            fontWeight: 'bold'
                          }}>
                            PRIMARY
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => removeMedia(index)}
                          style={{
                            position: 'absolute',
                            top: '0.5rem',
                            right: '0.5rem',
                            background: 'rgba(220, 38, 38, 0.9)',
                            border: 'none',
                            color: 'white',
                            cursor: 'pointer',
                            borderRadius: '50%',
                            width: '28px',
                            height: '28px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 0
                          }}
                        >
                          <X size={14} />
                        </button>
                      </div>

                      {/* Media Details */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', minWidth: 0 }}>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                          <button
                            type="button"
                            onClick={() => setPrimaryImage(index)}
                            style={{
                              padding: '0.5rem 1rem',
                              borderRadius: '6px',
                              border: media.is_primary ? '1px solid var(--color-sky-blue)' : '1px solid var(--color-gray-300)',
                              backgroundColor: media.is_primary ? 'var(--color-sky-blue)' : 'white',
                              color: media.is_primary ? 'white' : 'var(--color-gray-700)',
                              fontSize: '0.875rem',
                              fontWeight: 500,
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                            }}
                          >
                            {media.is_primary ? 'Primary Media' : 'Set as Primary'}
                          </button>
                        </div>

                        <div>
                          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', color: 'var(--color-gray-700)' }}>
                            Type
                          </label>
                          <select
                            value={media.type || 'image'}
                            onChange={(e) => updateMediaField(index, 'type', e.target.value)}
                            className="input"
                            style={{ width: '100%' }}
                          >
                            <option value="image">image</option>
                            <option value="video">video</option>
                            <option value="model3d">model3d</option>
                            <option value="document">document</option>
                          </select>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)', gap: '0.75rem' }}>
                          <div style={{ minWidth: 0 }}>
                            <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', color: 'var(--color-gray-700)' }}>
                              Width (px)
                            </label>
                            <input
                              type="number"
                              min="0"
                              value={media.width || ''}
                              onChange={(e) => updateMediaField(index, 'width', parseInt(e.target.value) || null)}
                              className="input"
                              placeholder="Width"
                            />
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', color: 'var(--color-gray-700)' }}>
                              Height (px)
                            </label>
                            <input
                              type="number"
                              min="0"
                              value={media.height || ''}
                              onChange={(e) => updateMediaField(index, 'height', parseInt(e.target.value) || null)}
                              className="input"
                              placeholder="Height"
                            />
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', color: 'var(--color-gray-700)' }}>
                              MIME Type
                            </label>
                            <select
                              value={media.mime_type || 'image/jpeg'}
                              onChange={(e) => updateMediaField(index, 'mime_type', e.target.value)}
                              className="input"
                            >
                              <option value="image/jpeg">image/jpeg</option>
                              <option value="image/png">image/png</option>
                              <option value="image/gif">image/gif</option>
                              <option value="image/webp">image/webp</option>
                              <option value="image/svg+xml">image/svg+xml</option>
                              <option value="video/mp4">video/mp4</option>
                              <option value="video/webm">video/webm</option>
                              <option value="video/quicktime">video/quicktime</option>
                            </select>
                          </div>
                        </div>

                        <div>
                          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', color: 'var(--color-gray-700)' }}>
                            Caption
                          </label>
                          <input
                            type="text"
                            value={media.caption || ''}
                            onChange={(e) => updateMediaField(index, 'caption', e.target.value)}
                            className="input"
                            placeholder="Media caption"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Variants Section */}
                    <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--color-gray-300)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                        <label style={{ fontSize: '0.875rem', fontWeight: 'bold', color: 'var(--color-gray-700)' }}>
                          Variants
                        </label>
                        <div>
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={(e) => handleVariantUpload(index, e)}
                            style={{ display: 'none' }}
                            id={`variant-upload-${index}`}
                          />
                          <label
                            htmlFor={`variant-upload-${index}`}
                            style={{
                              display: 'inline-block',
                              padding: '0.375rem 0.75rem',
                              borderRadius: '6px',
                              border: '1px solid var(--color-gray-300)',
                              backgroundColor: 'white',
                              color: 'var(--color-gray-700)',
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                            }}
                          >
                            <Plus size={12} style={{ display: 'inline', marginRight: '0.25rem', verticalAlign: 'middle' }} />
                            Add Variant
                          </label>
                        </div>
                      </div>
                      {media.variants && media.variants.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          {media.variants.map((variant: any, variantIndex: number) => (
                            <div
                              key={variantIndex}
                              style={{
                                border: '1px solid var(--color-gray-300)',
                                borderRadius: '6px',
                                padding: '0.75rem',
                                backgroundColor: 'var(--color-gray-50)'
                              }}
                            >
                              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '150px minmax(0, 1fr)', gap: '0.75rem' }}>
                                {/* Variant Preview */}
                                <div style={{ position: 'relative', minWidth: 0 }}>
                                  {variant.url ? (
                                    <img
                                      src={variant.url}
                                      alt={`Variant ${variantIndex + 1}`}
                                      style={{
                                        width: '100%',
                                        height: '120px',
                                        objectFit: 'cover',
                                        borderRadius: '4px',
                                        border: '1px solid var(--color-gray-300)'
                                      }}
                                      onError={(e) => {
                                        (e.target as HTMLImageElement).style.display = 'none'
                                      }}
                                    />
                                  ) : (
                                    <div style={{
                                      width: '100%',
                                      height: '120px',
                                      borderRadius: '4px',
                                      border: '1px solid var(--color-gray-300)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      backgroundColor: 'white',
                                      color: 'var(--color-gray-500)',
                                      fontSize: '0.75rem'
                                    }}>
                                      No image
                                    </div>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => removeVariant(index, variantIndex)}
                                    style={{
                                      position: 'absolute',
                                      top: '0.25rem',
                                      right: '0.25rem',
                                      background: 'rgba(220, 38, 38, 0.9)',
                                      border: 'none',
                                      color: 'white',
                                      cursor: 'pointer',
                                      borderRadius: '50%',
                                      width: '20px',
                                      height: '20px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      padding: 0
                                    }}
                                  >
                                    <X size={10} />
                                  </button>
                                </div>

                                {/* Variant Details */}
                                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)', gap: '0.5rem', minWidth: 0 }}>
                                  <div style={{ minWidth: 0 }}>
                                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem', color: 'var(--color-gray-700)' }}>
                                      URL
                                    </label>
                                    <input
                                      type="text"
                                      value={variant.url || ''}
                                      onChange={(e) => updateVariantField(index, variantIndex, 'url', e.target.value)}
                                      className="input"
                                      placeholder="Variant URL"
                                      style={{ fontSize: '0.75rem', padding: '0.375rem', width: '100%', minWidth: 0, boxSizing: 'border-box' }}
                                    />
                                  </div>
                                  <div style={{ minWidth: 0 }}>
                                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem', color: 'var(--color-gray-700)' }}>
                                      Width
                                    </label>
                                    <input
                                      type="number"
                                      min="0"
                                      value={variant.width || ''}
                                      onChange={(e) => updateVariantField(index, variantIndex, 'width', parseInt(e.target.value) || null)}
                                      className="input"
                                      placeholder="Width"
                                      style={{ fontSize: '0.75rem', padding: '0.375rem', width: '100%', minWidth: 0, boxSizing: 'border-box' }}
                                    />
                                  </div>
                                  <div style={{ minWidth: 0 }}>
                                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem', color: 'var(--color-gray-700)' }}>
                                      Height
                                    </label>
                                    <input
                                      type="number"
                                      min="0"
                                      value={variant.height || ''}
                                      onChange={(e) => updateVariantField(index, variantIndex, 'height', parseInt(e.target.value) || null)}
                                      className="input"
                                      placeholder="Height"
                                      style={{ fontSize: '0.75rem', padding: '0.375rem', width: '100%', minWidth: 0, boxSizing: 'border-box' }}
                                    />
                                  </div>
                                  <div style={{ minWidth: 0 }}>
                                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem', color: 'var(--color-gray-700)' }}>
                                      MIME Type
                                    </label>
                                    <select
                                      value={variant.mime_type || 'image/jpeg'}
                                      onChange={(e) => updateVariantField(index, variantIndex, 'mime_type', e.target.value)}
                                      className="input"
                                      style={{ fontSize: '0.75rem', padding: '0.375rem', width: '100%', minWidth: 0, boxSizing: 'border-box' }}
                                    >
                                      <option value="image/jpeg">image/jpeg</option>
                                      <option value="image/png">image/png</option>
                                      <option value="image/gif">image/gif</option>
                                      <option value="image/webp">image/webp</option>
                                      <option value="image/svg+xml">image/svg+xml</option>
                                    </select>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>

        {/* Section 4: Translations */}
        <div style={{
          marginBottom: '2rem',
          padding: '1.5rem',
          backgroundColor: 'var(--color-gray-50)',
          borderRadius: '12px',
          border: '1px solid var(--color-gray-200)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h3 style={{
              margin: 0,
              fontSize: '1.125rem',
              fontWeight: 600,
              color: 'var(--color-gray-900)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <div style={{
                width: '4px',
                height: '20px',
                backgroundColor: 'var(--color-sky-blue)',
                borderRadius: '2px'
              }} />
              Translations
            </h3>
            <button
              type="button"
              onClick={addLocalizedContent}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                border: '1px solid var(--color-gray-300)',
                backgroundColor: 'white',
                color: 'var(--color-gray-700)',
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
            >
              <Plus size={16} />
              Add Translation
            </button>
          </div>

          {formData.content.localized_content && formData.content.localized_content.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {formData.content.localized_content.map((localized: any, index: number) => (
                <div
                  key={index}
                  style={{
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: '8px',
                    padding: '1rem',
                    backgroundColor: 'var(--color-gray-50)'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h4 style={{ margin: 0, fontSize: '0.9rem', color: 'var(--color-gray-700)' }}>
                      Translation {index + 1}
                    </h4>
                    <button
                      type="button"
                      onClick={() => removeLocalizedContent(index)}
                      style={{
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        border: '1px solid var(--color-red-500)',
                        backgroundColor: 'white',
                        color: 'var(--color-red-500)',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <X size={14} style={{ display: 'inline', verticalAlign: 'middle' }} />
                      Remove
                    </button>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                        Locale * (e.g., es-US, fr-CA)
                      </label>
                      <input
                        type="text"
                        value={localized.locale || ''}
                        onChange={(e) => updateLocalizedContent(index, 'locale', e.target.value)}
                        className="input"
                        required
                        placeholder="es-US"
                        pattern="^[a-z]{2}-[A-Z]{2}$"
                        style={{ textTransform: 'none' }}
                      />
                      <p style={{ fontSize: '0.75rem', color: 'var(--color-gray-600)', marginTop: '0.25rem' }}>
                        Format: language code (lowercase) - country code (uppercase)
                      </p>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                        Title *
                      </label>
                      <input
                        type="text"
                        value={localized.title || ''}
                        onChange={(e) => updateLocalizedContent(index, 'title', e.target.value)}
                        className="input"
                        required
                        maxLength={200}
                        placeholder="Translated title"
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                        Subtitle
                      </label>
                      <input
                        type="text"
                        value={localized.subtitle || ''}
                        onChange={(e) => updateLocalizedContent(index, 'subtitle', e.target.value)}
                        className="input"
                        maxLength={200}
                        placeholder="Translated subtitle"
                      />
                    </div>
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                      Description *
                    </label>
                    <textarea
                      value={localized.description || ''}
                      onChange={(e) => updateLocalizedContent(index, 'description', e.target.value)}
                      className="input"
                      required
                      rows={4}
                      maxLength={5000}
                      style={{ width: '100%' }}
                      placeholder="Translated description"
                    />
                  </div>

                  {/* Localized Bullet Points */}
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                      Bullet Points
                    </label>
                    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <input
                        type="text"
                        value={localizedBulletPoints[index] || ''}
                        onChange={(e) => setLocalizedBulletPoints({ ...localizedBulletPoints, [index]: e.target.value })}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                            addLocalizedBulletPoint(index)
                          }
                        }}
                        className="input"
                        placeholder="Add bullet point..."
                        style={{ flex: 1 }}
                      />
                      <button
                        type="button"
                        onClick={() => addLocalizedBulletPoint(index)}
                        className="button button-secondary"
                      >
                        Add
                      </button>
                    </div>
                    {localized.bullet_points && localized.bullet_points.length > 0 && (
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {localized.bullet_points.map((bp: string, bpIndex: number) => (
                          <li
                            key={bpIndex}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              padding: '0.5rem',
                              marginBottom: '0.25rem',
                              backgroundColor: 'white',
                              borderRadius: '4px',
                              border: '1px solid var(--color-gray-300)'
                            }}
                          >
                            <span style={{ flex: 1 }}>{bp}</span>
                            <button
                              type="button"
                              onClick={() => removeLocalizedBulletPoint(index, bpIndex)}
                              style={{
                                padding: '0.25rem',
                                border: 'none',
                                background: 'transparent',
                                color: 'var(--color-red-500)',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <X size={14} />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* End Section 4: Translations */}

        {/* Section 5: Classification */}
        <div style={{
          marginBottom: '2rem',
          padding: '1.5rem',
          backgroundColor: 'var(--color-gray-50)',
          borderRadius: '12px',
          border: '1px solid var(--color-gray-200)'
        }}>
          <h3 style={{
            marginBottom: '1.5rem',
            fontSize: '1.125rem',
            fontWeight: 600,
            color: 'var(--color-gray-900)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <div style={{
              width: '4px',
              height: '20px',
              backgroundColor: 'var(--color-sky-blue)',
              borderRadius: '2px'
            }} />
            Classification
          </h3>

          <div style={{ marginBottom: '1rem', width: '100%' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
              Category Path * (e.g., `Travel & Tours`{'>'}` Adventure`{'>'}` Hiking`)
            </label>
            <input
              type="text"
              value={formData.taxonomy.category_path}
              onChange={(e) => setFormData({
                ...formData,
                taxonomy: { ...formData.taxonomy, category_path: e.target.value }
              })}
              className="input"
              required
              placeholder="Travel & Tours > Adventure > Hiking"
              style={{ width: '100%' }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                Category Code
              </label>
              <input
                type="text"
                value={formData.taxonomy.category_code}
                onChange={(e) => setFormData({
                  ...formData,
                  taxonomy: { ...formData.taxonomy, category_code: e.target.value }
                })}
                className="input"
                placeholder="e.g., TRAV-ADV-HIK"
                style={{ width: '100%' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                Provider
              </label>
              <input
                type="text"
                value={formData.taxonomy.brand}
                onChange={(e) => setFormData({
                  ...formData,
                  taxonomy: { ...formData.taxonomy, brand: e.target.value }
                })}
                className="input"
                maxLength={70}
                placeholder="Experience provider name"
                style={{ width: '100%' }}
              />
            </div>
          </div>

          {/* Attributes */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
              Attributes
            </label>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: isMobile ? 'wrap' : 'nowrap', alignItems: 'flex-end' }}>
              <input
                type="text"
                value={attributeName}
                onChange={(e) => setAttributeName(e.target.value)}
                className="input"
                placeholder="Attribute name (e.g., Duration, Group Size)"
                style={{ flex: isMobile ? '1 1 100%' : '2 1 auto', minWidth: 0 }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && attributeName.trim() && attributeValue.trim()) {
                    e.preventDefault()
                    addAttribute()
                  }
                }}
              />
              <input
                type="text"
                value={attributeValue}
                onChange={(e) => setAttributeValue(e.target.value)}
                className="input"
                placeholder="Value"
                style={{ flex: isMobile ? '1 1 100%' : '1 1 120px', minWidth: 0, maxWidth: isMobile ? '100%' : '150px' }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && attributeName.trim() && attributeValue.trim()) {
                    e.preventDefault()
                    addAttribute()
                  }
                }}
              />
              <input
                type="text"
                value={attributeUnit}
                onChange={(e) => setAttributeUnit(e.target.value)}
                className="input"
                placeholder="Unit (e.g., hours, days, km)"
                style={{ flex: isMobile ? '1 1 100%' : '1 1 100px', minWidth: 0 }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && attributeName.trim() && attributeValue.trim()) {
                    e.preventDefault()
                    addAttribute()
                  }
                }}
              />
              <button
                type="button"
                onClick={addAttribute}
                className="button button-secondary"
                style={{
                  flex: isMobile ? '1 1 100%' : '0 0 auto',
                  whiteSpace: 'nowrap',
                  padding: '0.625rem 1rem'
                }}
              >
                Add Attribute
              </button>
            </div>
            {formData.taxonomy.attributes && formData.taxonomy.attributes.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {formData.taxonomy.attributes.map((attr: any, index: number) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.5rem',
                      backgroundColor: 'var(--color-gray-50)',
                      borderRadius: '4px',
                      border: '1px solid var(--color-gray-300)'
                    }}
                  >
                    <span style={{ flex: 1, fontSize: '0.875rem' }}>
                      <strong>{attr.name}:</strong> {attr.value} {attr.unit ? `(${attr.unit})` : ''}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeAttribute(index)}
                      style={{
                        padding: '0.25rem',
                        border: 'none',
                        background: 'transparent',
                        color: 'var(--color-red-500)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Target Audience */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
              Target Audience
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', color: 'var(--color-gray-700)' }}>
                  Age Range
                </label>
                <input
                  type="text"
                  value={formData.taxonomy.target_audience?.age_range || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    taxonomy: {
                      ...formData.taxonomy,
                      target_audience: { ...formData.taxonomy.target_audience, age_range: e.target.value }
                    }
                  })}
                  className="input"
                  placeholder="e.g., 18-65"
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', color: 'var(--color-gray-700)' }}>
                  Gender
                </label>
                <select
                  value={formData.taxonomy.target_audience?.gender || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    taxonomy: {
                      ...formData.taxonomy,
                      target_audience: { ...formData.taxonomy.target_audience, gender: e.target.value }
                    }
                  })}
                  className="input"
                  style={{ width: '100%' }}
                >
                  <option value="">Select...</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="unisex">Unisex</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Section 6: Compliance */}
        <div style={{
          marginBottom: '2rem',
          padding: '1.5rem',
          backgroundColor: 'var(--color-gray-50)',
          borderRadius: '12px',
          border: '1px solid var(--color-gray-200)'
        }}>
          <h3 style={{
            marginBottom: '1.5rem',
            fontSize: '1.125rem',
            fontWeight: 600,
            color: 'var(--color-gray-900)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <div style={{
              width: '4px',
              height: '20px',
              backgroundColor: 'var(--color-sky-blue)',
              borderRadius: '2px'
            }} />
            Compliance
          </h3>

          {/* Warnings */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
              Warnings
            </label>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <input
                type="text"
                value={warning}
                onChange={(e) => setWarning(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addWarning()
                  }
                }}
                className="input"
                placeholder="Add warning (e.g., Not suitable for children under 12)"
                style={{ flex: 1 }}
              />
              <button
                type="button"
                onClick={addWarning}
                className="button button-secondary"
              >
                Add
              </button>
            </div>
            {formData.compliance?.warnings && formData.compliance.warnings.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {formData.compliance.warnings.map((warn: string, index: number) => (
                  <div key={index} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.5rem',
                    backgroundColor: 'var(--color-gray-50)',
                    borderRadius: '4px',
                    border: '1px solid var(--color-gray-300)'
                  }}>
                    <span style={{ fontSize: '0.875rem' }}>{warn}</span>
                    <button
                      type="button"
                      onClick={() => removeWarning(index)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#dc2626',
                        cursor: 'pointer',
                        padding: '0.25rem'
                      }}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Age Restriction and Regulated */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                Age Restriction (minimum age)
              </label>
              <input
                type="number"
                min="0"
                value={formData.compliance?.age_restriction || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  compliance: {
                    ...formData.compliance,
                    age_restriction: e.target.value ? parseInt(e.target.value) : undefined
                  }
                })}
                className="input"
                placeholder="e.g., 18"
                style={{ width: '100%' }}
              />
              <p style={{ fontSize: '0.75rem', color: 'var(--color-gray-600)', marginTop: '0.25rem' }}>
                Minimum age required for this experience
              </p>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                Regulated Activity
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={formData.compliance?.regulated || false}
                  onChange={(e) => setFormData({
                    ...formData,
                    compliance: {
                      ...formData.compliance,
                      regulated: e.target.checked
                    }
                  })}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span style={{ fontSize: '0.875rem', color: 'var(--color-gray-700)' }}>
                  This experience is regulated by local authorities
                </span>
              </div>
            </div>
          </div>

          {/* Regulatory Details */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
              Regulatory Details
            </label>
            <textarea
              value={formData.compliance?.regulatory_details || ''}
              onChange={(e) => setFormData({
                ...formData,
                compliance: {
                  ...formData.compliance,
                  regulatory_details: e.target.value
                }
              })}
              className="input"
              rows={3}
              maxLength={1000}
              style={{ width: '100%' }}
              placeholder="Enter any regulatory information, licenses, or compliance requirements..."
            />
            <p style={{ fontSize: '0.75rem', color: 'var(--color-gray-600)', marginTop: '0.25rem' }}>
              Details about licenses, permits, or regulatory compliance requirements
            </p>
          </div>
        </div>

        {/* Section 7: Availability & Seasonality */}
        <div style={{
          marginBottom: '2rem',
          padding: '1.5rem',
          backgroundColor: 'var(--color-gray-50)',
          borderRadius: '12px',
          border: '1px solid var(--color-gray-200)'
        }}>
          <h3 style={{
            marginBottom: '1.5rem',
            fontSize: '1.125rem',
            fontWeight: 600,
            color: 'var(--color-gray-900)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <div style={{
              width: '4px',
              height: '20px',
              backgroundColor: 'var(--color-sky-blue)',
              borderRadius: '2px'
            }} />
            Availability & Seasonality
          </h3>

          {/* Release Date and Discontinue Date */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                Available From
              </label>
              <input
                type="date"
                value={formData.lifecycle?.release_date || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  lifecycle: {
                    ...formData.lifecycle,
                    release_date: e.target.value
                  }
                })}
                className="input"
                style={{ width: '100%' }}
              />
              <p style={{ fontSize: '0.75rem', color: 'var(--color-gray-600)', marginTop: '0.25rem' }}>
                Date when this experience becomes available
              </p>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                Available Until
              </label>
              <input
                type="date"
                value={formData.lifecycle?.discontinue_date || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  lifecycle: {
                    ...formData.lifecycle,
                    discontinue_date: e.target.value
                  }
                })}
                className="input"
                style={{ width: '100%' }}
              />
              <p style={{ fontSize: '0.75rem', color: 'var(--color-gray-600)', marginTop: '0.25rem' }}>
                Date when this experience will no longer be available
              </p>
            </div>
          </div>

          {/* Seasonality */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
              Seasonality
            </label>
            <select
              value={formData.lifecycle?.seasonality || ''}
              onChange={(e) => setFormData({
                ...formData,
                lifecycle: {
                  ...formData.lifecycle,
                  seasonality: e.target.value
                }
              })}
              className="input"
              style={{ width: '100%' }}
            >
              <option value="">Select seasonality...</option>
              <option value="year-round">Year-Round</option>
              <option value="spring">Spring (March - May)</option>
              <option value="summer">Summer (June - August)</option>
              <option value="fall">Fall/Autumn (September - November)</option>
              <option value="winter">Winter (December - February)</option>
              <option value="peak-season">Peak Season Only</option>
              <option value="off-season">Off-Season Only</option>
              <option value="seasonal">Seasonal (Custom)</option>
            </select>
            <p style={{ fontSize: '0.75rem', color: 'var(--color-gray-600)', marginTop: '0.25rem' }}>
              When this experience is typically available throughout the year
            </p>
          </div>
        </div>

        {/* Section 8: Review and Ratings */}
        <div style={{
          marginBottom: '2rem',
          padding: '1.5rem',
          backgroundColor: 'var(--color-gray-50)',
          borderRadius: '12px',
          border: '1px solid var(--color-gray-200)'
        }}>
          <h3 style={{
            marginBottom: '1.5rem',
            fontSize: '1.125rem',
            fontWeight: 600,
            color: 'var(--color-gray-900)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <div style={{
              width: '4px',
              height: '20px',
              backgroundColor: 'var(--color-sky-blue)',
              borderRadius: '2px'
            }} />
            Review and Ratings
          </h3>

          {/* Average Rating and Review Count */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                Average Rating *
              </label>
              <input
                type="number"
                min="0"
                max="5"
                step="0.1"
                value={formData.performance?.average_rating || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  performance: {
                    ...formData.performance,
                    average_rating: e.target.value ? parseFloat(e.target.value) : undefined
                  }
                })}
                className="input"
                placeholder="e.g., 4.5"
                style={{ width: '100%' }}
              />
              <p style={{ fontSize: '0.75rem', color: 'var(--color-gray-600)', marginTop: '0.25rem' }}>
                Average customer rating (0-5 stars)
              </p>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                Review Count
              </label>
              <input
                type="number"
                min="0"
                value={formData.performance?.review_count || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  performance: {
                    ...formData.performance,
                    review_count: e.target.value ? parseInt(e.target.value) : undefined
                  }
                })}
                className="input"
                placeholder="e.g., 125"
                style={{ width: '100%' }}
              />
              <p style={{ fontSize: '0.75rem', color: 'var(--color-gray-600)', marginTop: '0.25rem' }}>
                Total number of customer reviews
              </p>
            </div>
          </div>

          {/* Popularity Score and Return Rate */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                Popularity Score
              </label>
              <input
                type="number"
                min="0"
                max="5"
                step="0.1"
                value={formData.performance?.popularity_score || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  performance: {
                    ...formData.performance,
                    popularity_score: e.target.value ? parseFloat(e.target.value) : undefined
                  }
                })}
                className="input"
                placeholder="e.g., 4.2"
                style={{ width: '100%' }}
              />
              <p style={{ fontSize: '0.75rem', color: 'var(--color-gray-600)', marginTop: '0.25rem' }}>
                Popularity rating based on bookings and views (0-5)
              </p>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                Return Rate (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={formData.performance?.return_rate || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  performance: {
                    ...formData.performance,
                    return_rate: e.target.value ? parseFloat(e.target.value) : undefined
                  }
                })}
                className="input"
                placeholder="e.g., 85.5"
                style={{ width: '100%' }}
              />
              <p style={{ fontSize: '0.75rem', color: 'var(--color-gray-600)', marginTop: '0.25rem' }}>
                Percentage of customers who book again (0-100%)
              </p>
            </div>
          </div>
        </div>

        {/* Section 9: Pricing & Availability */}
        <div style={{
          marginBottom: '2rem',
          padding: '1.5rem',
          backgroundColor: 'var(--color-gray-50)',
          borderRadius: '12px',
          border: '1px solid var(--color-gray-200)'
        }}>
          <h3 style={{
            marginBottom: '1.5rem',
            fontSize: '1.125rem',
            fontWeight: 600,
            color: 'var(--color-gray-900)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <div style={{
              width: '4px',
              height: '20px',
              backgroundColor: 'var(--color-sky-blue)',
              borderRadius: '2px'
            }} />
            Fares
          </h3>

          {formData.offers.map((offer: any, offerIndex: number) => (
            <div key={offerIndex} style={{
              padding: '1rem',
              backgroundColor: 'var(--color-gray-50)',
              borderRadius: '8px',
              marginBottom: '1rem'
            }}>
              {/* Offer ID and Merchant Listing ID - Only show when editing.
                  iter-9c: rendered read-only. These are system-managed
                  identifiers (DB primary key and external feed code). Marking
                  them `required` previously blocked saves on every Bokun-
                  imported product whose feed did not populate
                  offer_id_external (e.g. product 4334). Vendors can see them
                  for support purposes but cannot edit or blank them. */}
              {product && (
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem', color: 'var(--color-gray-600)' }}>
                      Offer ID
                    </label>
                    <input
                      type="text"
                      value={offer.offer_id || ''}
                      readOnly
                      tabIndex={-1}
                      aria-readonly="true"
                      className="input"
                      style={{
                        backgroundColor: 'var(--color-gray-100)',
                        color: 'var(--color-gray-600)',
                        cursor: 'not-allowed',
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem', color: 'var(--color-gray-600)' }}>
                      Merchant Listing ID
                    </label>
                    <input
                      type="text"
                      value={offer.merchant_listing_id || ''}
                      readOnly
                      tabIndex={-1}
                      aria-readonly="true"
                      className="input"
                      style={{
                        backgroundColor: 'var(--color-gray-100)',
                        color: 'var(--color-gray-600)',
                        cursor: 'not-allowed',
                      }}
                    />
                  </div>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                    Currency *
                  </label>
                  <input
                    type="text"
                    value={offer.currency}
                    onChange={(e) => {
                      const updatedOffers = [...formData.offers]
                      updatedOffers[offerIndex] = { ...offer, currency: e.target.value.toUpperCase() }
                      setFormData({ ...formData, offers: updatedOffers })
                    }}
                    className="input"
                    required
                    pattern="^[A-Z]{3}$"
                    placeholder="USD"
                    maxLength={3}
                    style={{ width: '100%' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                    Adult Price *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={offer.price}
                    onChange={(e) => {
                      const updatedOffers = [...formData.offers]
                      updatedOffers[offerIndex] = { ...offer, price: parseFloat(e.target.value) || 0 }
                      setFormData({ ...formData, offers: updatedOffers })
                    }}
                    className="input"
                    required
                    style={{ width: '100%' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                    Availability Status *
                  </label>
                  <select
                    value={offer.availability?.status || 'in_stock'}
                    onChange={(e) => {
                      const updatedOffers = [...formData.offers]
                      updatedOffers[offerIndex] = {
                        ...offer,
                        availability: { ...offer.availability, status: e.target.value }
                      }
                      setFormData({ ...formData, offers: updatedOffers })
                    }}
                    className="input"
                    required
                    style={{ width: '100%' }}
                  >
                    <option value="in_stock">In Stock</option>
                    <option value="out_of_stock">Out of Stock</option>
                    <option value="backorder">Backorder</option>
                    <option value="preorder">Preorder</option>
                    <option value="discontinued">Discontinued</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                    List Price (Original)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={offer.list_price || ''}
                    onChange={(e) => {
                      const updatedOffers = [...formData.offers]
                      updatedOffers[offerIndex] = { ...offer, list_price: e.target.value ? parseFloat(e.target.value) : undefined }
                      setFormData({ ...formData, offers: updatedOffers })
                    }}
                    className="input"
                    placeholder="Original price"
                    style={{ width: '100%' }}
                  />
                </div>
              </div>

              {/* Sale Information */}
              <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: 'white', borderRadius: '8px', border: '1px solid var(--color-gray-300)' }}>
                <h4 style={{ marginBottom: '1rem', fontSize: '0.95rem', fontWeight: 600, color: 'var(--color-gray-900)' }}>
                  Sale/Promotion
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                      Sale Price
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={offer.sale?.sale_price || ''}
                      onChange={(e) => {
                        const updatedOffers = [...formData.offers]
                        updatedOffers[offerIndex] = {
                          ...offer,
                          sale: {
                            ...offer.sale,
                            sale_price: e.target.value ? parseFloat(e.target.value) : undefined
                          }
                        }
                        setFormData({ ...formData, offers: updatedOffers })
                      }}
                      className="input"
                      placeholder="Discounted price"
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                      Sale Start Date
                    </label>
                    <input
                      type="datetime-local"
                      value={offer.sale?.start_time || ''}
                      onChange={(e) => {
                        const updatedOffers = [...formData.offers]
                        updatedOffers[offerIndex] = {
                          ...offer,
                          sale: {
                            ...offer.sale,
                            start_time: e.target.value
                          }
                        }
                        setFormData({ ...formData, offers: updatedOffers })
                      }}
                      className="input"
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                      Sale End Date
                    </label>
                    <input
                      type="datetime-local"
                      value={offer.sale?.end_time || ''}
                      onChange={(e) => {
                        const updatedOffers = [...formData.offers]
                        updatedOffers[offerIndex] = {
                          ...offer,
                          sale: {
                            ...offer.sale,
                            end_time: e.target.value
                          }
                        }
                        setFormData({ ...formData, offers: updatedOffers })
                      }}
                      className="input"
                      style={{ width: '100%' }}
                    />
                  </div>
                </div>
              </div>

              {/* Tax Information */}
              <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: 'white', borderRadius: '8px', border: '1px solid var(--color-gray-300)' }}>
                <h4 style={{ marginBottom: '1rem', fontSize: '0.95rem', fontWeight: 600, color: 'var(--color-gray-900)' }}>
                  Tax Information
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                      Tax Included
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={offer.tax?.tax_included || false}
                        onChange={(e) => {
                          const updatedOffers = [...formData.offers]
                          updatedOffers[offerIndex] = {
                            ...offer,
                            tax: {
                              ...offer.tax,
                              tax_included: e.target.checked
                            }
                          }
                          setFormData({ ...formData, offers: updatedOffers })
                        }}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '0.875rem', color: 'var(--color-gray-700)' }}>
                        Tax is included in price
                      </span>
                    </div>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                      Tax Rate (%)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={offer.tax?.tax_rate || ''}
                      onChange={(e) => {
                        const updatedOffers = [...formData.offers]
                        updatedOffers[offerIndex] = {
                          ...offer,
                          tax: {
                            ...offer.tax,
                            tax_rate: e.target.value ? parseFloat(e.target.value) : undefined
                          }
                        }
                        setFormData({ ...formData, offers: updatedOffers })
                      }}
                      className="input"
                      placeholder="e.g., 10.5"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                      Tax Region
                    </label>
                    <input
                      type="text"
                      value={offer.tax?.tax_region || ''}
                      onChange={(e) => {
                        const updatedOffers = [...formData.offers]
                        updatedOffers[offerIndex] = {
                          ...offer,
                          tax: {
                            ...offer.tax,
                            tax_region: e.target.value
                          }
                        }
                        setFormData({ ...formData, offers: updatedOffers })
                      }}
                      className="input"
                      placeholder="e.g., US-CA, EU"
                    />
                  </div>
                </div>
              </div>

              {/* Availability Details */}
              <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: 'white', borderRadius: '8px', border: '1px solid var(--color-gray-300)' }}>
                <h4 style={{ marginBottom: '1rem', fontSize: '0.95rem', fontWeight: 600, color: 'var(--color-gray-900)' }}>
                  Availability Details
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                      Available Spots
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={offer.availability?.stock_quantity || ''}
                      onChange={(e) => {
                        const updatedOffers = [...formData.offers]
                        updatedOffers[offerIndex] = {
                          ...offer,
                          availability: {
                            ...offer.availability,
                            stock_quantity: e.target.value ? parseInt(e.target.value) : undefined
                          }
                        }
                        setFormData({ ...formData, offers: updatedOffers })
                      }}
                      className="input"
                      placeholder="Number of spots available"
                      style={{ width: '100%' }}
                    />
                    <p style={{ fontSize: '0.75rem', color: 'var(--color-gray-600)', marginTop: '0.25rem' }}>
                      Total number of spots/seats available
                    </p>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                      Booking Lead Time (Days)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={offer.availability?.lead_time_days || ''}
                      onChange={(e) => {
                        const updatedOffers = [...formData.offers]
                        updatedOffers[offerIndex] = {
                          ...offer,
                          availability: {
                            ...offer.availability,
                            lead_time_days: e.target.value ? parseInt(e.target.value) : undefined
                          }
                        }
                        setFormData({ ...formData, offers: updatedOffers })
                      }}
                      className="input"
                      placeholder="e.g., 7"
                      style={{ width: '100%' }}
                    />
                    <p style={{ fontSize: '0.75rem', color: 'var(--color-gray-600)', marginTop: '0.25rem' }}>
                      Minimum days in advance for booking
                    </p>
                  </div>
                </div>
              </div>

              {/* Service Options & Meeting Points */}
              <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: 'white', borderRadius: '8px', border: '1px solid var(--color-gray-300)' }}>
                <h4 style={{ marginBottom: '1rem', fontSize: '0.95rem', fontWeight: 600, color: 'var(--color-gray-900)' }}>
                  Service Options & Meeting Points
                </h4>

                {/* Service Options (adapted from shipping_options) */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', fontWeight: 'bold', fontSize: '0.9rem' }}>
                      Service Options / Tour Packages
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        const updatedOffers = [...formData.offers]
                        updatedOffers[offerIndex] = {
                          ...offer,
                          fulfillment: {
                            ...offer.fulfillment,
                            shipping_options: [...(offer.fulfillment?.shipping_options || []), {
                              region: '',
                              service_level: '',
                              price: 0,
                              currency: offer.currency || 'USD',
                              estimated_min_days: undefined,
                              estimated_max_days: undefined,
                            }]
                          }
                        }
                        setFormData({ ...formData, offers: updatedOffers })
                      }}
                      style={{
                        padding: '0.375rem 0.75rem',
                        borderRadius: '6px',
                        border: '1px solid var(--color-gray-300)',
                        backgroundColor: 'white',
                        color: 'var(--color-gray-700)',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        cursor: 'pointer',
                      }}
                    >
                      <Plus size={12} style={{ display: 'inline', marginRight: '0.25rem', verticalAlign: 'middle' }} />
                      Add Service Option
                    </button>
                  </div>
                  {offer.fulfillment?.shipping_options && offer.fulfillment.shipping_options.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      {offer.fulfillment.shipping_options.map((option: any, optionIndex: number) => (
                        <div key={optionIndex} style={{
                          padding: '1rem',
                          backgroundColor: 'var(--color-gray-50)',
                          borderRadius: '8px',
                          border: '1px solid var(--color-gray-300)'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                            <h5 style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-gray-900)' }}>
                              Service Option {optionIndex + 1}
                            </h5>
                            <button
                              type="button"
                              onClick={() => {
                                const updatedOffers = [...formData.offers]
                                updatedOffers[offerIndex] = {
                                  ...offer,
                                  fulfillment: {
                                    ...offer.fulfillment,
                                    shipping_options: offer.fulfillment.shipping_options.filter((_: any, i: number) => i !== optionIndex)
                                  }
                                }
                                setFormData({ ...formData, offers: updatedOffers })
                              }}
                              style={{
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                border: '1px solid var(--color-red-500)',
                                backgroundColor: 'white',
                                color: 'var(--color-red-500)',
                                fontSize: '0.75rem',
                                cursor: 'pointer',
                              }}
                            >
                              <X size={12} />
                            </button>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                            <div>
                              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', color: 'var(--color-gray-700)' }}>
                                Region *
                              </label>
                              <input
                                type="text"
                                value={option.region || ''}
                                onChange={(e) => {
                                  const updatedOffers = [...formData.offers]
                                  const updatedOptions = [...offer.fulfillment.shipping_options]
                                  updatedOptions[optionIndex] = { ...option, region: e.target.value }
                                  updatedOffers[offerIndex] = {
                                    ...offer,
                                    fulfillment: {
                                      ...offer.fulfillment,
                                      shipping_options: updatedOptions
                                    }
                                  }
                                  setFormData({ ...formData, offers: updatedOffers })
                                }}
                                className="input"
                                placeholder="e.g., US, EU, Global"
                                style={{ width: '100%' }}
                                required
                              />
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', color: 'var(--color-gray-700)' }}>
                                Service Level / Package Name *
                              </label>
                              <input
                                type="text"
                                value={option.service_level || ''}
                                onChange={(e) => {
                                  const updatedOffers = [...formData.offers]
                                  const updatedOptions = [...offer.fulfillment.shipping_options]
                                  updatedOptions[optionIndex] = { ...option, service_level: e.target.value }
                                  updatedOffers[offerIndex] = {
                                    ...offer,
                                    fulfillment: {
                                      ...offer.fulfillment,
                                      shipping_options: updatedOptions
                                    }
                                  }
                                  setFormData({ ...formData, offers: updatedOffers })
                                }}
                                className="input"
                                placeholder="e.g., Standard, Premium, VIP"
                                style={{ width: '100%' }}
                                required
                              />
                            </div>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr 1fr', gap: '0.75rem' }}>
                            <div>
                              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', color: 'var(--color-gray-700)' }}>
                                Price *
                              </label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={option.price || ''}
                                onChange={(e) => {
                                  const updatedOffers = [...formData.offers]
                                  const updatedOptions = [...offer.fulfillment.shipping_options]
                                  updatedOptions[optionIndex] = { ...option, price: parseFloat(e.target.value) || 0 }
                                  updatedOffers[offerIndex] = {
                                    ...offer,
                                    fulfillment: {
                                      ...offer.fulfillment,
                                      shipping_options: updatedOptions
                                    }
                                  }
                                  setFormData({ ...formData, offers: updatedOffers })
                                }}
                                className="input"
                                style={{ width: '100%' }}
                                required
                              />
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', color: 'var(--color-gray-700)' }}>
                                Currency *
                              </label>
                              <input
                                type="text"
                                value={option.currency || ''}
                                onChange={(e) => {
                                  const updatedOffers = [...formData.offers]
                                  const updatedOptions = [...offer.fulfillment.shipping_options]
                                  updatedOptions[optionIndex] = { ...option, currency: e.target.value.toUpperCase() }
                                  updatedOffers[offerIndex] = {
                                    ...offer,
                                    fulfillment: {
                                      ...offer.fulfillment,
                                      shipping_options: updatedOptions
                                    }
                                  }
                                  setFormData({ ...formData, offers: updatedOffers })
                                }}
                                className="input"
                                placeholder="USD"
                                maxLength={3}
                                pattern="^[A-Z]{3}$"
                                style={{ width: '100%' }}
                                required
                              />
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', color: 'var(--color-gray-700)' }}>
                                Duration (Min Days)
                              </label>
                              <input
                                type="number"
                                min="0"
                                value={option.estimated_min_days || ''}
                                onChange={(e) => {
                                  const updatedOffers = [...formData.offers]
                                  const updatedOptions = [...offer.fulfillment.shipping_options]
                                  updatedOptions[optionIndex] = { ...option, estimated_min_days: e.target.value ? parseInt(e.target.value) : undefined }
                                  updatedOffers[offerIndex] = {
                                    ...offer,
                                    fulfillment: {
                                      ...offer.fulfillment,
                                      shipping_options: updatedOptions
                                    }
                                  }
                                  setFormData({ ...formData, offers: updatedOffers })
                                }}
                                className="input"
                                placeholder="Min"
                                style={{ width: '100%' }}
                              />
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', color: 'var(--color-gray-700)' }}>
                                Duration (Max Days)
                              </label>
                              <input
                                type="number"
                                min="0"
                                value={option.estimated_max_days || ''}
                                onChange={(e) => {
                                  const updatedOffers = [...formData.offers]
                                  const updatedOptions = [...offer.fulfillment.shipping_options]
                                  updatedOptions[optionIndex] = { ...option, estimated_max_days: e.target.value ? parseInt(e.target.value) : undefined }
                                  updatedOffers[offerIndex] = {
                                    ...offer,
                                    fulfillment: {
                                      ...offer.fulfillment,
                                      shipping_options: updatedOptions
                                    }
                                  }
                                  setFormData({ ...formData, offers: updatedOffers })
                                }}
                                className="input"
                                placeholder="Max"
                                style={{ width: '100%' }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Meeting Points (adapted from pickup) */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={offer.fulfillment?.pickup?.enabled || false}
                        onChange={(e) => {
                          const updatedOffers = [...formData.offers]
                          updatedOffers[offerIndex] = {
                            ...offer,
                            fulfillment: {
                              ...offer.fulfillment,
                              pickup: {
                                ...offer.fulfillment?.pickup,
                                enabled: e.target.checked
                              }
                            }
                          }
                          setFormData({ ...formData, offers: updatedOffers })
                        }}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <label style={{ fontWeight: 'bold', fontSize: '0.9rem', cursor: 'pointer' }}>
                        Enable Meeting Points / Departure Locations
                      </label>
                    </div>
                    {offer.fulfillment?.pickup?.enabled && (
                      <button
                        type="button"
                        onClick={() => {
                          const updatedOffers = [...formData.offers]
                          updatedOffers[offerIndex] = {
                            ...offer,
                            fulfillment: {
                              ...offer.fulfillment,
                              pickup: {
                                ...offer.fulfillment?.pickup,
                                locations: [...(offer.fulfillment?.pickup?.locations || []), {
                                  location_id: 'LOC-' + crypto.randomUUID().replace(/-/g, '').substring(0, 8),
                                  name: '',
                                  address: {
                                    street: '',
                                    city: '',
                                    state: '',
                                    postal_code: '',
                                    country_code: '',
                                    latitude: undefined,
                                    longitude: undefined,
                                  },
                                  phone: '',
                                  hours: [],
                                }]
                              }
                            }
                          }
                          setFormData({ ...formData, offers: updatedOffers })
                        }}
                        style={{
                          padding: '0.375rem 0.75rem',
                          borderRadius: '6px',
                          border: '1px solid var(--color-gray-300)',
                          backgroundColor: 'white',
                          color: 'var(--color-gray-700)',
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          cursor: 'pointer',
                        }}
                      >
                        <Plus size={12} style={{ display: 'inline', marginRight: '0.25rem', verticalAlign: 'middle' }} />
                        Add Location
                      </button>
                    )}
                  </div>
                  {offer.fulfillment?.pickup?.enabled && offer.fulfillment.pickup.locations && offer.fulfillment.pickup.locations.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      {offer.fulfillment.pickup.locations.map((location: any, locationIndex: number) => (
                        <div key={locationIndex} style={{
                          padding: '1rem',
                          backgroundColor: 'var(--color-gray-50)',
                          borderRadius: '8px',
                          border: '1px solid var(--color-gray-300)'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                            <h5 style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-gray-900)' }}>
                              Meeting Point {locationIndex + 1}
                            </h5>
                            <button
                              type="button"
                              onClick={() => {
                                const updatedOffers = [...formData.offers]
                                updatedOffers[offerIndex] = {
                                  ...offer,
                                  fulfillment: {
                                    ...offer.fulfillment,
                                    pickup: {
                                      ...offer.fulfillment?.pickup,
                                      locations: offer.fulfillment.pickup.locations.filter((_: any, i: number) => i !== locationIndex)
                                    }
                                  }
                                }
                                setFormData({ ...formData, offers: updatedOffers })
                              }}
                              style={{
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                border: '1px solid var(--color-red-500)',
                                backgroundColor: 'white',
                                color: 'var(--color-red-500)',
                                fontSize: '0.75rem',
                                cursor: 'pointer',
                              }}
                            >
                              <X size={12} />
                            </button>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                            <div>
                              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', color: 'var(--color-gray-700)' }}>
                                Location Name *
                              </label>
                              <input
                                type="text"
                                value={location.name || ''}
                                onChange={(e) => {
                                  const updatedOffers = [...formData.offers]
                                  const updatedLocations = [...offer.fulfillment.pickup.locations]
                                  updatedLocations[locationIndex] = { ...location, name: e.target.value }
                                  updatedOffers[offerIndex] = {
                                    ...offer,
                                    fulfillment: {
                                      ...offer.fulfillment,
                                      pickup: {
                                        ...offer.fulfillment?.pickup,
                                        locations: updatedLocations
                                      }
                                    }
                                  }
                                  setFormData({ ...formData, offers: updatedOffers })
                                }}
                                className="input"
                                placeholder="e.g., Central Station, Hotel Lobby"
                                style={{ width: '100%' }}
                                required
                              />
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', color: 'var(--color-gray-700)' }}>
                                Phone
                              </label>
                              <input
                                type="text"
                                value={location.phone || ''}
                                onChange={(e) => {
                                  const updatedOffers = [...formData.offers]
                                  const updatedLocations = [...offer.fulfillment.pickup.locations]
                                  updatedLocations[locationIndex] = { ...location, phone: e.target.value }
                                  updatedOffers[offerIndex] = {
                                    ...offer,
                                    fulfillment: {
                                      ...offer.fulfillment,
                                      pickup: {
                                        ...offer.fulfillment?.pickup,
                                        locations: updatedLocations
                                      }
                                    }
                                  }
                                  setFormData({ ...formData, offers: updatedOffers })
                                }}
                                className="input"
                                placeholder="e.g., +1-555-0123"
                                style={{ width: '100%' }}
                              />
                            </div>
                          </div>
                          <div style={{ marginBottom: '0.75rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-gray-900)' }}>
                              Address
                            </label>
                            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                              <div>
                                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', color: 'var(--color-gray-700)' }}>
                                  Street
                                </label>
                                <input
                                  type="text"
                                  value={location.address?.street || ''}
                                  onChange={(e) => {
                                    const updatedOffers = [...formData.offers]
                                    const updatedLocations = [...offer.fulfillment.pickup.locations]
                                    updatedLocations[locationIndex] = {
                                      ...location,
                                      address: { ...location.address, street: e.target.value }
                                    }
                                    updatedOffers[offerIndex] = {
                                      ...offer,
                                      fulfillment: {
                                        ...offer.fulfillment,
                                        pickup: {
                                          ...offer.fulfillment?.pickup,
                                          locations: updatedLocations
                                        }
                                      }
                                    }
                                    setFormData({ ...formData, offers: updatedOffers })
                                  }}
                                  className="input"
                                  placeholder="Street address"
                                  style={{ width: '100%' }}
                                />
                              </div>
                              <div>
                                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', color: 'var(--color-gray-700)' }}>
                                  City
                                </label>
                                <input
                                  type="text"
                                  value={location.address?.city || ''}
                                  onChange={(e) => {
                                    const updatedOffers = [...formData.offers]
                                    const updatedLocations = [...offer.fulfillment.pickup.locations]
                                    updatedLocations[locationIndex] = {
                                      ...location,
                                      address: { ...location.address, city: e.target.value }
                                    }
                                    updatedOffers[offerIndex] = {
                                      ...offer,
                                      fulfillment: {
                                        ...offer.fulfillment,
                                        pickup: {
                                          ...offer.fulfillment?.pickup,
                                          locations: updatedLocations
                                        }
                                      }
                                    }
                                    setFormData({ ...formData, offers: updatedOffers })
                                  }}
                                  className="input"
                                  placeholder="City"
                                  style={{ width: '100%' }}
                                />
                              </div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '0.75rem' }}>
                              <div>
                                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', color: 'var(--color-gray-700)' }}>
                                  State/Province
                                </label>
                                <input
                                  type="text"
                                  value={location.address?.state || ''}
                                  onChange={(e) => {
                                    const updatedOffers = [...formData.offers]
                                    const updatedLocations = [...offer.fulfillment.pickup.locations]
                                    updatedLocations[locationIndex] = {
                                      ...location,
                                      address: { ...location.address, state: e.target.value }
                                    }
                                    updatedOffers[offerIndex] = {
                                      ...offer,
                                      fulfillment: {
                                        ...offer.fulfillment,
                                        pickup: {
                                          ...offer.fulfillment?.pickup,
                                          locations: updatedLocations
                                        }
                                      }
                                    }
                                    setFormData({ ...formData, offers: updatedOffers })
                                  }}
                                  className="input"
                                  placeholder="State"
                                  style={{ width: '100%' }}
                                />
                              </div>
                              <div>
                                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', color: 'var(--color-gray-700)' }}>
                                  Postal Code
                                </label>
                                <input
                                  type="text"
                                  value={location.address?.postal_code || ''}
                                  onChange={(e) => {
                                    const updatedOffers = [...formData.offers]
                                    const updatedLocations = [...offer.fulfillment.pickup.locations]
                                    updatedLocations[locationIndex] = {
                                      ...location,
                                      address: { ...location.address, postal_code: e.target.value }
                                    }
                                    updatedOffers[offerIndex] = {
                                      ...offer,
                                      fulfillment: {
                                        ...offer.fulfillment,
                                        pickup: {
                                          ...offer.fulfillment?.pickup,
                                          locations: updatedLocations
                                        }
                                      }
                                    }
                                    setFormData({ ...formData, offers: updatedOffers })
                                  }}
                                  className="input"
                                  placeholder="Postal code"
                                  style={{ width: '100%' }}
                                />
                              </div>
                              <div>
                                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', color: 'var(--color-gray-700)' }}>
                                  Country Code *
                                </label>
                                <input
                                  type="text"
                                  value={location.address?.country_code || ''}
                                  onChange={(e) => {
                                    const updatedOffers = [...formData.offers]
                                    const updatedLocations = [...offer.fulfillment.pickup.locations]
                                    updatedLocations[locationIndex] = {
                                      ...location,
                                      address: { ...location.address, country_code: e.target.value.toUpperCase() }
                                    }
                                    updatedOffers[offerIndex] = {
                                      ...offer,
                                      fulfillment: {
                                        ...offer.fulfillment,
                                        pickup: {
                                          ...offer.fulfillment?.pickup,
                                          locations: updatedLocations
                                        }
                                      }
                                    }
                                    setFormData({ ...formData, offers: updatedOffers })
                                  }}
                                  className="input"
                                  placeholder="US"
                                  maxLength={2}
                                  pattern="^[A-Z]{2}$"
                                  style={{ width: '100%' }}
                                  required
                                />
                              </div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.75rem', marginTop: '0.75rem' }}>
                              <div>
                                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', color: 'var(--color-gray-700)' }}>
                                  Latitude
                                </label>
                                <input
                                  type="number"
                                  step="0.000001"
                                  value={location.address?.latitude || ''}
                                  onChange={(e) => {
                                    const updatedOffers = [...formData.offers]
                                    const updatedLocations = [...offer.fulfillment.pickup.locations]
                                    updatedLocations[locationIndex] = {
                                      ...location,
                                      address: { ...location.address, latitude: e.target.value ? parseFloat(e.target.value) : undefined }
                                    }
                                    updatedOffers[offerIndex] = {
                                      ...offer,
                                      fulfillment: {
                                        ...offer.fulfillment,
                                        pickup: {
                                          ...offer.fulfillment?.pickup,
                                          locations: updatedLocations
                                        }
                                      }
                                    }
                                    setFormData({ ...formData, offers: updatedOffers })
                                  }}
                                  className="input"
                                  placeholder="e.g., 37.7749"
                                  style={{ width: '100%' }}
                                />
                              </div>
                              <div>
                                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', color: 'var(--color-gray-700)' }}>
                                  Longitude
                                </label>
                                <input
                                  type="number"
                                  step="0.000001"
                                  value={location.address?.longitude || ''}
                                  onChange={(e) => {
                                    const updatedOffers = [...formData.offers]
                                    const updatedLocations = [...offer.fulfillment.pickup.locations]
                                    updatedLocations[locationIndex] = {
                                      ...location,
                                      address: { ...location.address, longitude: e.target.value ? parseFloat(e.target.value) : undefined }
                                    }
                                    updatedOffers[offerIndex] = {
                                      ...offer,
                                      fulfillment: {
                                        ...offer.fulfillment,
                                        pickup: {
                                          ...offer.fulfillment?.pickup,
                                          locations: updatedLocations
                                        }
                                      }
                                    }
                                    setFormData({ ...formData, offers: updatedOffers })
                                  }}
                                  className="input"
                                  placeholder="e.g., -122.4194"
                                  style={{ width: '100%' }}
                                />
                              </div>
                            </div>
                          </div>
                          {/* Operating Hours */}
                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                              <label style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-gray-900)' }}>
                                Operating Hours
                              </label>
                              <button
                                type="button"
                                onClick={() => {
                                  const updatedOffers = [...formData.offers]
                                  const updatedLocations = [...offer.fulfillment.pickup.locations]
                                  updatedLocations[locationIndex] = {
                                    ...location,
                                    hours: [...(location.hours || []), {
                                      day: 'mon',
                                      open: '09:00',
                                      close: '18:00'
                                    }]
                                  }
                                  updatedOffers[offerIndex] = {
                                    ...offer,
                                    fulfillment: {
                                      ...offer.fulfillment,
                                      pickup: {
                                        ...offer.fulfillment?.pickup,
                                        locations: updatedLocations
                                      }
                                    }
                                  }
                                  setFormData({ ...formData, offers: updatedOffers })
                                }}
                                style={{
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '4px',
                                  border: '1px solid var(--color-gray-300)',
                                  backgroundColor: 'white',
                                  color: 'var(--color-gray-700)',
                                  fontSize: '0.75rem',
                                  cursor: 'pointer',
                                }}
                              >
                                <Plus size={10} style={{ display: 'inline', marginRight: '0.25rem', verticalAlign: 'middle' }} />
                                Add Hours
                              </button>
                            </div>
                            {location.hours && location.hours.length > 0 && (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                {location.hours.map((hour: any, hourIndex: number) => (
                                  <div key={hourIndex} style={{
                                    display: 'grid',
                                    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr auto',
                                    gap: '0.5rem',
                                    alignItems: 'end'
                                  }}>
                                    <div>
                                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem', color: 'var(--color-gray-700)' }}>
                                        Day
                                      </label>
                                      <select
                                        value={hour.day || 'mon'}
                                        onChange={(e) => {
                                          const updatedOffers = [...formData.offers]
                                          const updatedLocations = [...offer.fulfillment.pickup.locations]
                                          const updatedHours = [...location.hours]
                                          updatedHours[hourIndex] = { ...hour, day: e.target.value }
                                          updatedLocations[locationIndex] = { ...location, hours: updatedHours }
                                          updatedOffers[offerIndex] = {
                                            ...offer,
                                            fulfillment: {
                                              ...offer.fulfillment,
                                              pickup: {
                                                ...offer.fulfillment?.pickup,
                                                locations: updatedLocations
                                              }
                                            }
                                          }
                                          setFormData({ ...formData, offers: updatedOffers })
                                        }}
                                        className="input"
                                        style={{ width: '100%' }}
                                      >
                                        <option value="mon">Monday</option>
                                        <option value="tue">Tuesday</option>
                                        <option value="wed">Wednesday</option>
                                        <option value="thu">Thursday</option>
                                        <option value="fri">Friday</option>
                                        <option value="sat">Saturday</option>
                                        <option value="sun">Sunday</option>
                                      </select>
                                    </div>
                                    <div>
                                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem', color: 'var(--color-gray-700)' }}>
                                        Open
                                      </label>
                                      <input
                                        type="time"
                                        value={hour.open || ''}
                                        onChange={(e) => {
                                          const updatedOffers = [...formData.offers]
                                          const updatedLocations = [...offer.fulfillment.pickup.locations]
                                          const updatedHours = [...location.hours]
                                          updatedHours[hourIndex] = { ...hour, open: e.target.value }
                                          updatedLocations[locationIndex] = { ...location, hours: updatedHours }
                                          updatedOffers[offerIndex] = {
                                            ...offer,
                                            fulfillment: {
                                              ...offer.fulfillment,
                                              pickup: {
                                                ...offer.fulfillment?.pickup,
                                                locations: updatedLocations
                                              }
                                            }
                                          }
                                          setFormData({ ...formData, offers: updatedOffers })
                                        }}
                                        className="input"
                                        style={{ width: '100%' }}
                                      />
                                    </div>
                                    <div>
                                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem', color: 'var(--color-gray-700)' }}>
                                        Close
                                      </label>
                                      <input
                                        type="time"
                                        value={hour.close || ''}
                                        onChange={(e) => {
                                          const updatedOffers = [...formData.offers]
                                          const updatedLocations = [...offer.fulfillment.pickup.locations]
                                          const updatedHours = [...location.hours]
                                          updatedHours[hourIndex] = { ...hour, close: e.target.value }
                                          updatedLocations[locationIndex] = { ...location, hours: updatedHours }
                                          updatedOffers[offerIndex] = {
                                            ...offer,
                                            fulfillment: {
                                              ...offer.fulfillment,
                                              pickup: {
                                                ...offer.fulfillment?.pickup,
                                                locations: updatedLocations
                                              }
                                            }
                                          }
                                          setFormData({ ...formData, offers: updatedOffers })
                                        }}
                                        className="input"
                                        style={{ width: '100%' }}
                                      />
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const updatedOffers = [...formData.offers]
                                        const updatedLocations = [...offer.fulfillment.pickup.locations]
                                        updatedLocations[locationIndex] = {
                                          ...location,
                                          hours: location.hours.filter((_: any, i: number) => i !== hourIndex)
                                        }
                                        updatedOffers[offerIndex] = {
                                          ...offer,
                                          fulfillment: {
                                            ...offer.fulfillment,
                                            pickup: {
                                              ...offer.fulfillment?.pickup,
                                              locations: updatedLocations
                                            }
                                          }
                                        }
                                        setFormData({ ...formData, offers: updatedOffers })
                                      }}
                                      style={{
                                        padding: '0.5rem',
                                        borderRadius: '4px',
                                        border: '1px solid var(--color-red-500)',
                                        backgroundColor: 'white',
                                        color: 'var(--color-red-500)',
                                        cursor: 'pointer',
                                        height: 'fit-content',
                                      }}
                                    >
                                      <X size={14} />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Geographic Overrides */}
              <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: 'white', borderRadius: '8px', border: '1px solid var(--color-gray-300)' }}>
                <h4 style={{ marginBottom: '1rem', fontSize: '0.95rem', fontWeight: 600, color: 'var(--color-gray-900)' }}>
                  Regional Pricing & Availability
                </h4>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                  <p style={{ fontSize: '0.875rem', color: 'var(--color-gray-600)', margin: 0 }}>
                    Override pricing and availability for specific regions/countries
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      const updatedOffers = [...formData.offers]
                      updatedOffers[offerIndex] = {
                        ...offer,
                        geographic_overrides: [...(offer.geographic_overrides || []), {
                          region: '',
                          currency: offer.currency || 'USD',
                          price: 0,
                          availability_status: 'in_stock',
                          shipping_options: [],
                        }]
                      }
                      setFormData({ ...formData, offers: updatedOffers })
                    }}
                    style={{
                      padding: '0.375rem 0.75rem',
                      borderRadius: '6px',
                      border: '1px solid var(--color-gray-300)',
                      backgroundColor: 'white',
                      color: 'var(--color-gray-700)',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}
                  >
                    <Plus size={12} style={{ display: 'inline', marginRight: '0.25rem', verticalAlign: 'middle' }} />
                    Add Regional Override
                  </button>
                </div>
                {offer.geographic_overrides && offer.geographic_overrides.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {offer.geographic_overrides.map((override: any, overrideIndex: number) => (
                      <div key={overrideIndex} style={{
                        padding: '1rem',
                        backgroundColor: 'var(--color-gray-50)',
                        borderRadius: '8px',
                        border: '1px solid var(--color-gray-300)'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                          <h5 style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-gray-900)' }}>
                            Regional Override {overrideIndex + 1}
                          </h5>
                          <button
                            type="button"
                            onClick={() => {
                              const updatedOffers = [...formData.offers]
                              updatedOffers[offerIndex] = {
                                ...offer,
                                geographic_overrides: offer.geographic_overrides.filter((_: any, i: number) => i !== overrideIndex)
                              }
                              setFormData({ ...formData, offers: updatedOffers })
                            }}
                            style={{
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              border: '1px solid var(--color-red-500)',
                              backgroundColor: 'white',
                              color: 'var(--color-red-500)',
                              fontSize: '0.75rem',
                              cursor: 'pointer',
                            }}
                          >
                            <X size={12} />
                          </button>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                          <div>
                            <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', color: 'var(--color-gray-700)' }}>
                              Region/Country Code *
                            </label>
                            <input
                              type="text"
                              value={override.region || ''}
                              onChange={(e) => {
                                const updatedOffers = [...formData.offers]
                                const updatedOverrides = [...offer.geographic_overrides]
                                updatedOverrides[overrideIndex] = { ...override, region: e.target.value.toUpperCase() }
                                updatedOffers[offerIndex] = {
                                  ...offer,
                                  geographic_overrides: updatedOverrides
                                }
                                setFormData({ ...formData, offers: updatedOffers })
                              }}
                              className="input"
                              placeholder="e.g., CA, GB, US"
                              maxLength={2}
                              style={{ width: '100%' }}
                              required
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', color: 'var(--color-gray-700)' }}>
                              Currency *
                            </label>
                            <input
                              type="text"
                              value={override.currency || ''}
                              onChange={(e) => {
                                const updatedOffers = [...formData.offers]
                                const updatedOverrides = [...offer.geographic_overrides]
                                updatedOverrides[overrideIndex] = { ...override, currency: e.target.value.toUpperCase() }
                                updatedOffers[offerIndex] = {
                                  ...offer,
                                  geographic_overrides: updatedOverrides
                                }
                                setFormData({ ...formData, offers: updatedOffers })
                              }}
                              className="input"
                              placeholder="CAD"
                              maxLength={3}
                              pattern="^[A-Z]{3}$"
                              style={{ width: '100%' }}
                              required
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', color: 'var(--color-gray-700)' }}>
                              Price *
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={override.price || ''}
                              onChange={(e) => {
                                const updatedOffers = [...formData.offers]
                                const updatedOverrides = [...offer.geographic_overrides]
                                updatedOverrides[overrideIndex] = { ...override, price: parseFloat(e.target.value) || 0 }
                                updatedOffers[offerIndex] = {
                                  ...offer,
                                  geographic_overrides: updatedOverrides
                                }
                                setFormData({ ...formData, offers: updatedOffers })
                              }}
                              className="input"
                              style={{ width: '100%' }}
                              required
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', color: 'var(--color-gray-700)' }}>
                              Availability Status *
                            </label>
                            <select
                              value={override.availability_status || 'in_stock'}
                              onChange={(e) => {
                                const updatedOffers = [...formData.offers]
                                const updatedOverrides = [...offer.geographic_overrides]
                                updatedOverrides[overrideIndex] = { ...override, availability_status: e.target.value }
                                updatedOffers[offerIndex] = {
                                  ...offer,
                                  geographic_overrides: updatedOverrides
                                }
                                setFormData({ ...formData, offers: updatedOffers })
                              }}
                              className="input"
                              style={{ width: '100%' }}
                              required
                            >
                              <option value="in_stock">In Stock</option>
                              <option value="out_of_stock">Out of Stock</option>
                              <option value="preorder">Preorder</option>
                              <option value="backorder">Backorder</option>
                            </select>
                          </div>
                        </div>
                        {/* Service Options for this region */}
                        <div style={{ marginTop: '0.75rem' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                            <label style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-gray-900)' }}>
                              Regional Service Options
                            </label>
                            <button
                              type="button"
                              onClick={() => {
                                const updatedOffers = [...formData.offers]
                                const updatedOverrides = [...offer.geographic_overrides]
                                updatedOverrides[overrideIndex] = {
                                  ...override,
                                  shipping_options: [...(override.shipping_options || []), {
                                    region: override.region || '',
                                    service_level: '',
                                    price: 0,
                                    currency: override.currency || 'USD',
                                    estimated_min_days: undefined,
                                    estimated_max_days: undefined,
                                  }]
                                }
                                updatedOffers[offerIndex] = {
                                  ...offer,
                                  geographic_overrides: updatedOverrides
                                }
                                setFormData({ ...formData, offers: updatedOffers })
                              }}
                              style={{
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                border: '1px solid var(--color-gray-300)',
                                backgroundColor: 'white',
                                color: 'var(--color-gray-700)',
                                fontSize: '0.75rem',
                                cursor: 'pointer',
                              }}
                            >
                              <Plus size={10} style={{ display: 'inline', marginRight: '0.25rem', verticalAlign: 'middle' }} />
                              Add Service Option
                            </button>
                          </div>
                          {override.shipping_options && override.shipping_options.length > 0 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                              {override.shipping_options.map((option: any, optionIndex: number) => (
                                <div key={optionIndex} style={{
                                  padding: '0.75rem',
                                  backgroundColor: 'white',
                                  borderRadius: '6px',
                                  border: '1px solid var(--color-gray-200)'
                                }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                                    <span style={{ fontSize: '0.75rem', fontWeight: 500, color: 'var(--color-gray-700)' }}>
                                      Service Option {optionIndex + 1}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const updatedOffers = [...formData.offers]
                                        const updatedOverrides = [...offer.geographic_overrides]
                                        updatedOverrides[overrideIndex] = {
                                          ...override,
                                          shipping_options: override.shipping_options.filter((_: any, i: number) => i !== optionIndex)
                                        }
                                        updatedOffers[offerIndex] = {
                                          ...offer,
                                          geographic_overrides: updatedOverrides
                                        }
                                        setFormData({ ...formData, offers: updatedOffers })
                                      }}
                                      style={{
                                        padding: '0.25rem 0.5rem',
                                        borderRadius: '4px',
                                        border: '1px solid var(--color-red-500)',
                                        backgroundColor: 'white',
                                        color: 'var(--color-red-500)',
                                        fontSize: '0.75rem',
                                        cursor: 'pointer',
                                      }}
                                    >
                                      <X size={10} />
                                    </button>
                                  </div>
                                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr 1fr', gap: '0.5rem' }}>
                                    <div>
                                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem', color: 'var(--color-gray-700)' }}>
                                        Service Level *
                                      </label>
                                      <input
                                        type="text"
                                        value={option.service_level || ''}
                                        onChange={(e) => {
                                          const updatedOffers = [...formData.offers]
                                          const updatedOverrides = [...offer.geographic_overrides]
                                          const updatedOptions = [...override.shipping_options]
                                          updatedOptions[optionIndex] = { ...option, service_level: e.target.value }
                                          updatedOverrides[overrideIndex] = { ...override, shipping_options: updatedOptions }
                                          updatedOffers[offerIndex] = {
                                            ...offer,
                                            geographic_overrides: updatedOverrides
                                          }
                                          setFormData({ ...formData, offers: updatedOffers })
                                        }}
                                        className="input"
                                        placeholder="Standard, Premium"
                                        style={{ width: '100%', fontSize: '0.875rem' }}
                                        required
                                      />
                                    </div>
                                    <div>
                                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem', color: 'var(--color-gray-700)' }}>
                                        Price *
                                      </label>
                                      <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={option.price || ''}
                                        onChange={(e) => {
                                          const updatedOffers = [...formData.offers]
                                          const updatedOverrides = [...offer.geographic_overrides]
                                          const updatedOptions = [...override.shipping_options]
                                          updatedOptions[optionIndex] = { ...option, price: parseFloat(e.target.value) || 0 }
                                          updatedOverrides[overrideIndex] = { ...override, shipping_options: updatedOptions }
                                          updatedOffers[offerIndex] = {
                                            ...offer,
                                            geographic_overrides: updatedOverrides
                                          }
                                          setFormData({ ...formData, offers: updatedOffers })
                                        }}
                                        className="input"
                                        style={{ width: '100%', fontSize: '0.875rem' }}
                                        required
                                      />
                                    </div>
                                    <div>
                                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem', color: 'var(--color-gray-700)' }}>
                                        Currency *
                                      </label>
                                      <input
                                        type="text"
                                        value={option.currency || ''}
                                        onChange={(e) => {
                                          const updatedOffers = [...formData.offers]
                                          const updatedOverrides = [...offer.geographic_overrides]
                                          const updatedOptions = [...override.shipping_options]
                                          updatedOptions[optionIndex] = { ...option, currency: e.target.value.toUpperCase() }
                                          updatedOverrides[overrideIndex] = { ...override, shipping_options: updatedOptions }
                                          updatedOffers[offerIndex] = {
                                            ...offer,
                                            geographic_overrides: updatedOverrides
                                          }
                                          setFormData({ ...formData, offers: updatedOffers })
                                        }}
                                        className="input"
                                        placeholder="CAD"
                                        maxLength={3}
                                        pattern="^[A-Z]{3}$"
                                        style={{ width: '100%', fontSize: '0.875rem' }}
                                        required
                                      />
                                    </div>
                                    <div>
                                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem', color: 'var(--color-gray-700)' }}>
                                        Duration (Days)
                                      </label>
                                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.25rem' }}>
                                        <input
                                          type="number"
                                          min="0"
                                          value={option.estimated_min_days || ''}
                                          onChange={(e) => {
                                            const updatedOffers = [...formData.offers]
                                            const updatedOverrides = [...offer.geographic_overrides]
                                            const updatedOptions = [...override.shipping_options]
                                            updatedOptions[optionIndex] = { ...option, estimated_min_days: e.target.value ? parseInt(e.target.value) : undefined }
                                            updatedOverrides[overrideIndex] = { ...override, shipping_options: updatedOptions }
                                            updatedOffers[offerIndex] = {
                                              ...offer,
                                              geographic_overrides: updatedOverrides
                                            }
                                            setFormData({ ...formData, offers: updatedOffers })
                                          }}
                                          className="input"
                                          placeholder="Min"
                                          style={{ width: '100%', fontSize: '0.875rem' }}
                                        />
                                        <input
                                          type="number"
                                          min="0"
                                          value={option.estimated_max_days || ''}
                                          onChange={(e) => {
                                            const updatedOffers = [...formData.offers]
                                            const updatedOverrides = [...offer.geographic_overrides]
                                            const updatedOptions = [...override.shipping_options]
                                            updatedOptions[optionIndex] = { ...option, estimated_max_days: e.target.value ? parseInt(e.target.value) : undefined }
                                            updatedOverrides[overrideIndex] = { ...override, shipping_options: updatedOptions }
                                            updatedOffers[offerIndex] = {
                                              ...offer,
                                              geographic_overrides: updatedOverrides
                                            }
                                            setFormData({ ...formData, offers: updatedOffers })
                                          }}
                                          className="input"
                                          placeholder="Max"
                                          style={{ width: '100%', fontSize: '0.875rem' }}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Section: Payments and Checkout */}
        <div style={{
          marginBottom: '2rem',
          padding: '1.5rem',
          backgroundColor: 'var(--color-gray-50)',
          borderRadius: '12px',
        }}>
          <h3 style={{
            marginBottom: '1rem',
            fontSize: '1.1rem',
            fontWeight: 600,
            color: 'var(--color-gray-900)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <div style={{
              width: '4px',
              height: '20px',
              backgroundColor: 'var(--color-sky-blue)',
              borderRadius: '2px'
            }} />
            Payments and Checkout
          </h3>

          {/* Accepted Payment Methods */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
              Accepted Payment Methods
            </label>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: '0.75rem'
            }}>
              {[
                { value: 'visa', label: 'Visa' },
                { value: 'mastercard', label: 'Mastercard' },
                { value: 'amex', label: 'American Express' },
                { value: 'paypal', label: 'PayPal' },
                { value: 'apple_pay', label: 'Apple Pay' },
                { value: 'google_pay', label: 'Google Pay' },
                { value: 'klarna', label: 'Klarna' },
              ].map((method) => (
                <div key={method.value} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={(formData.payments?.accepted_methods || []).includes(method.value)}
                    onChange={(e) => {
                      const currentMethods = formData.payments?.accepted_methods || []
                      const updatedMethods = e.target.checked
                        ? [...currentMethods, method.value]
                        : currentMethods.filter((m: string) => m !== method.value)
                      setFormData({
                        ...formData,
                        payments: {
                          ...formData.payments,
                          accepted_methods: updatedMethods
                        }
                      })
                    }}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <label style={{
                    fontSize: '0.875rem',
                    color: 'var(--color-gray-700)',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}>
                    {method.label}
                  </label>
                </div>
              ))}
            </div>
            {(!formData.payments?.accepted_methods || formData.payments.accepted_methods.length === 0) && (
              <p style={{ fontSize: '0.75rem', color: 'var(--color-gray-500)', marginTop: '0.5rem', fontStyle: 'italic' }}>
                No payment methods selected
              </p>
            )}
          </div>

          {/* Instant Checkout */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.75rem',
            backgroundColor: 'white',
            borderRadius: '8px',
            border: '1px solid var(--color-gray-300)'
          }}>
            <input
              type="checkbox"
              checked={formData.payments?.instant_checkout_enabled || false}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  payments: {
                    ...formData.payments,
                    instant_checkout_enabled: e.target.checked
                  }
                })
              }}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <div style={{ flex: 1 }}>
              <label style={{
                fontSize: '0.9rem',
                fontWeight: 600,
                color: 'var(--color-gray-900)',
                cursor: 'pointer',
                display: 'block',
                marginBottom: '0.25rem'
              }}>
                Enable Instant Checkout
              </label>
              <p style={{
                fontSize: '0.75rem',
                color: 'var(--color-gray-600)',
                margin: 0
              }}>
                Allow customers to complete purchases with one click using saved payment information
              </p>
            </div>
          </div>
        </div>

        {/* Section: Extensions */}
        <div style={{
          marginBottom: '2rem',
          padding: '1.5rem',
          backgroundColor: 'var(--color-gray-50)',
          borderRadius: '12px',
        }}>
          <h3 style={{
            marginBottom: '1rem',
            fontSize: '1.1rem',
            fontWeight: 600,
            color: 'var(--color-gray-900)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <div style={{
              width: '4px',
              height: '20px',
              backgroundColor: 'var(--color-sky-blue)',
              borderRadius: '2px'
            }} />
            Extensions
          </h3>
          <p style={{ fontSize: '0.875rem', color: 'var(--color-gray-600)', marginBottom: '1rem' }}>
            Add custom key-value pairs for additional metadata, SEO keywords, or integration-specific fields
          </p>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', fontWeight: 'bold', fontSize: '0.9rem' }}>
              Custom Fields
            </label>
            <button
              type="button"
              onClick={() => {
                const currentExtensions = formData.extensions || {}
                const newKey = `custom_field_${Object.keys(currentExtensions).length + 1}`
                setFormData({
                  ...formData,
                  extensions: {
                    ...currentExtensions,
                    [newKey]: ''
                  }
                })
              }}
              style={{
                padding: '0.375rem 0.75rem',
                borderRadius: '6px',
                border: '1px solid var(--color-gray-300)',
                backgroundColor: 'white',
                color: 'var(--color-gray-700)',
                fontSize: '0.75rem',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              <Plus size={12} style={{ display: 'inline', marginRight: '0.25rem', verticalAlign: 'middle' }} />
              Add Field
            </button>
          </div>
          {formData.extensions && Object.keys(formData.extensions).length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {Object.entries(formData.extensions).map(([key, value], index) => (
                <div key={index} style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : '2fr 3fr auto',
                  gap: '0.75rem',
                  alignItems: 'end',
                  padding: '0.75rem',
                  backgroundColor: 'white',
                  borderRadius: '6px',
                  border: '1px solid var(--color-gray-300)'
                }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', color: 'var(--color-gray-700)' }}>
                      Key
                    </label>
                    <input
                      type="text"
                      value={key}
                      onChange={(e) => {
                        const currentExtensions = formData.extensions || {}
                        const newExtensions: any = {}
                        const oldValue = currentExtensions[key]
                        // Remove old key and add new key
                        Object.keys(currentExtensions).forEach(k => {
                          if (k !== key) {
                            newExtensions[k] = currentExtensions[k]
                          }
                        })
                        newExtensions[e.target.value] = oldValue
                        setFormData({
                          ...formData,
                          extensions: newExtensions
                        })
                      }}
                      className="input"
                      placeholder="e.g., seo_keywords, custom_field"
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', color: 'var(--color-gray-700)' }}>
                      Value
                    </label>
                    <input
                      type="text"
                      value={value as string || ''}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          extensions: {
                            ...formData.extensions,
                            [key]: e.target.value
                          }
                        })
                      }}
                      className="input"
                      placeholder="e.g., adventure tours, hiking experiences"
                      style={{ width: '100%' }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const currentExtensions = formData.extensions || {}
                      const newExtensions: any = {}
                      Object.keys(currentExtensions).forEach(k => {
                        if (k !== key) {
                          newExtensions[k] = currentExtensions[k]
                        }
                      })
                      setFormData({
                        ...formData,
                        extensions: newExtensions
                      })
                    }}
                    style={{
                      padding: '0.5rem',
                      borderRadius: '4px',
                      border: '1px solid var(--color-red-500)',
                      backgroundColor: 'white',
                      color: 'var(--color-red-500)',
                      cursor: 'pointer',
                      height: 'fit-content',
                    }}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
          {(!formData.extensions || Object.keys(formData.extensions).length === 0) && (
            <p style={{ fontSize: '0.875rem', color: 'var(--color-gray-500)', fontStyle: 'italic', textAlign: 'center', padding: '1rem' }}>
              No custom fields added. Click "Add Field" to add key-value pairs.
            </p>
          )}
        </div>

        {/* Section: Related Experiences */}
        <div style={{
          marginBottom: '2rem',
          padding: '1.5rem',
          backgroundColor: 'var(--color-gray-50)',
          borderRadius: '12px',
        }}>
          <h3 style={{
            marginBottom: '1rem',
            fontSize: '1.1rem',
            fontWeight: 600,
            color: 'var(--color-gray-900)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <div style={{
              width: '4px',
              height: '20px',
              backgroundColor: 'var(--color-sky-blue)',
              borderRadius: '2px'
            }} />
            Related Experiences
          </h3>
          <p style={{ fontSize: '0.875rem', color: 'var(--color-gray-600)', marginBottom: '1rem' }}>
            Link this experience to other related experiences (e.g., complementary tours, upgrades, add-ons)
          </p>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', fontWeight: 'bold', fontSize: '0.9rem' }}>
              Linked Experiences
            </label>
            <button
              type="button"
              onClick={() => {
                setFormData({
                  ...formData,
                  related_products: [
                    ...(formData.related_products || []),
                    {
                      product_id: '',
                      relationship: 'complementary'
                    }
                  ]
                })
              }}
              style={{
                padding: '0.375rem 0.75rem',
                borderRadius: '6px',
                border: '1px solid var(--color-gray-300)',
                backgroundColor: 'white',
                color: 'var(--color-gray-700)',
                fontSize: '0.75rem',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              <Plus size={12} style={{ display: 'inline', marginRight: '0.25rem', verticalAlign: 'middle' }} />
              Add Related Experience
            </button>
          </div>
          {formData.related_products && formData.related_products.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {formData.related_products.map((related: any, index: number) => {
                // Find the related product details
                const relatedProduct = availableProducts.find((p: any) =>
                  p.product_id === related.product_id ||
                  p.product_id_external === related.product_id ||
                  p.external_product_id === related.product_id
                )
                return (
                  <div key={index} style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : '2fr 1.5fr auto',
                    gap: '0.75rem',
                    alignItems: 'end',
                    padding: '0.75rem',
                    backgroundColor: 'white',
                    borderRadius: '6px',
                    border: '1px solid var(--color-gray-300)'
                  }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', color: 'var(--color-gray-700)' }}>
                        Experience *
                      </label>
                      <select
                        value={related.product_id || ''}
                        onChange={(e) => {
                          const updatedRelated = [...formData.related_products]
                          updatedRelated[index] = {
                            ...related,
                            product_id: e.target.value
                          }
                          setFormData({
                            ...formData,
                            related_products: updatedRelated
                          })
                        }}
                        className="input"
                        style={{ width: '100%' }}
                        required
                      >
                        <option value="">-- Select an experience --</option>
                        {availableProducts
                          .filter((p: any) =>
                            // Exclude current product if editing
                            !product || (
                              p.product_id !== product.product_id &&
                              p.product_id_external !== product.product_id_external &&
                              p.external_product_id !== product.external_product_id
                            )
                          )
                          .map((p: any) => {
                            const productId = p.product_id || p.product_id_external || p.external_product_id
                            const title = p.content?.title || p.title || productId
                            return (
                              <option key={productId} value={productId}>
                                {title} {productId ? `(${productId})` : ''}
                              </option>
                            )
                          })}
                      </select>
                      {relatedProduct && (
                        <p style={{ fontSize: '0.75rem', color: 'var(--color-gray-600)', marginTop: '0.25rem' }}>
                          {relatedProduct.content?.title || relatedProduct.title || 'Selected experience'}
                        </p>
                      )}
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', color: 'var(--color-gray-700)' }}>
                        Relationship Type *
                      </label>
                      <select
                        value={related.relationship || 'complementary'}
                        onChange={(e) => {
                          const updatedRelated = [...formData.related_products]
                          updatedRelated[index] = {
                            ...related,
                            relationship: e.target.value
                          }
                          setFormData({
                            ...formData,
                            related_products: updatedRelated
                          })
                        }}
                        className="input"
                        style={{ width: '100%' }}
                        required
                      >
                        <option value="complementary">Complementary</option>
                        <option value="upgrade">Upgrade</option>
                        <option value="addon">Add-on</option>
                        <option value="cross_sell">Cross-sell</option>
                        <option value="bundle">Bundle</option>
                        <option value="alternative">Alternative</option>
                        <option value="accessory">Accessory</option>
                      </select>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setFormData({
                          ...formData,
                          related_products: formData.related_products.filter((_: any, i: number) => i !== index)
                        })
                      }}
                      style={{
                        padding: '0.5rem',
                        borderRadius: '4px',
                        border: '1px solid var(--color-red-500)',
                        backgroundColor: 'white',
                        color: 'var(--color-red-500)',
                        cursor: 'pointer',
                        height: 'fit-content',
                      }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                )
              })}
            </div>
          )}
          {(!formData.related_products || formData.related_products.length === 0) && (
            <p style={{ fontSize: '0.875rem', color: 'var(--color-gray-500)', fontStyle: 'italic', textAlign: 'center', padding: '1rem' }}>
              No related experiences linked. Click "Add Related Experience" to link experiences.
            </p>
          )}
        </div>

        {/* Storefront v3 fields (iter-3d) — written into extensions.storefront_v3 */}
        <ProductStorefrontV3Section
          extensions={formData.extensions}
          onChange={(next) => setFormData((prev: any) => ({ ...prev, extensions: next }))}
          suggestedFaqs={product?.storefront_faqs}
          faqsAreSuggestedDefaults={product?.faqs_are_suggested_defaults}
          productId={product?.product_id}
          faqsAutoMinimum={
            typeof product?.extensions?.storefront_v3?.faqs_auto_minimum === 'number'
              ? product.extensions.storefront_v3.faqs_auto_minimum
              : undefined
          }
        />
      </form>

      {/* Footer with Actions */}
      <div style={{
        display: 'flex',
        gap: '0.75rem',
        justifyContent: 'flex-end',
        padding: isMobile ? '1rem' : '1.25rem',
        borderTop: asModal ? '1px solid var(--color-gray-200)' : 'none',
        backgroundColor: asModal ? 'var(--color-gray-50)' : 'transparent',
        marginTop: asModal ? '0' : '2rem',
        borderBottomLeftRadius: asModal ? '16px' : '0',
        borderBottomRightRadius: asModal ? '16px' : '0',
      }}>
        {product && (
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              border: '1px solid var(--color-gray-300)',
              backgroundColor: 'white',
              color: 'var(--color-gray-700)',
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--color-gray-100)'
              e.currentTarget.style.borderColor = 'var(--color-gray-400)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'white'
              e.currentTarget.style.borderColor = 'var(--color-gray-300)'
            }}
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          form="product-form"
          disabled={isLoading}
          style={{
            padding: '0.75rem 1.5rem',
            borderRadius: '8px',
            border: 'none',
            backgroundColor: isLoading ? 'var(--color-gray-400)' : 'var(--color-sky-blue)',
            color: 'white',
            fontSize: '0.875rem',
            fontWeight: 600,
            cursor: isLoading ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            boxShadow: isLoading ? 'none' : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          }}
          onMouseEnter={(e) => {
            if (!isLoading) {
              e.currentTarget.style.backgroundColor = 'var(--color-sky-blue-dark, #0284c7)'
              e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
              e.currentTarget.style.transform = 'translateY(-1px)'
            }
          }}
          onMouseLeave={(e) => {
            if (!isLoading) {
              e.currentTarget.style.backgroundColor = 'var(--color-sky-blue)'
              e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
              e.currentTarget.style.transform = 'translateY(0)'
            }
          }}
        >
          {isLoading ? (
            <>
              <div style={{
                width: '14px',
                height: '14px',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                borderTopColor: 'white',
                borderRadius: '50%',
                animation: 'spin 0.6s linear infinite',
              }} />
              Saving...
            </>
          ) : (
            <>
              <Plus size={16} />
              {product ? 'Update Experience' : 'Create Experience'}
            </>
          )}
        </button>
      </div>
    </>
  )

  if (!asModal) {
    return formContent
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: isMobile ? '0.5rem' : '1rem',
        animation: 'fadeIn 0.2s ease-out',
      }}
      onClick={onCancel}
    >
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      <div
        className="card"
        style={{
          maxWidth: isMobile ? '100%' : '900px',
          width: '100%',
          maxHeight: '95vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'white',
          borderRadius: '16px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          animation: 'slideUp 0.3s ease-out',
          border: '1px solid rgba(0, 0, 0, 0.05)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: isMobile ? '1.25rem' : '1.5rem',
          borderBottom: '1px solid var(--color-gray-200)',
          backgroundColor: 'var(--color-gray-50)',
          borderTopLeftRadius: '16px',
          borderTopRightRadius: '16px',
        }}>
          <div>
            <h2 style={{
              margin: 0,
              fontSize: isMobile ? '1.25rem' : '1.5rem',
              fontWeight: 600,
              color: 'var(--color-gray-900)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}>
              <Package size={24} style={{ color: 'var(--color-sky-blue)' }} />
              {product ? 'Edit Experience' : 'Add New Experience'}
            </h2>
            <p style={{
              margin: '0.25rem 0 0 0',
              fontSize: '0.875rem',
              color: 'var(--color-gray-600)',
            }}>
              {product ? 'Update experience information' : 'Fill in the details to create a new experience'}
            </p>
          </div>
          <button
            onClick={onCancel}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0.5rem',
              minWidth: 'auto',
              width: '2.5rem',
              height: '2.5rem',
              background: 'white',
              border: '1px solid var(--color-gray-300)',
              borderRadius: '8px',
              cursor: 'pointer',
              color: 'var(--color-gray-600)',
              transition: 'all 0.2s ease',
            }}
            aria-label="Close"
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--color-gray-100)'
              e.currentTarget.style.borderColor = 'var(--color-gray-400)'
              e.currentTarget.style.color = 'var(--color-gray-900)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'white'
              e.currentTarget.style.borderColor = 'var(--color-gray-300)'
              e.currentTarget.style.color = 'var(--color-gray-600)'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Scrollable Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: isMobile ? '1.25rem' : '1.5rem',
          backgroundColor: 'white',
        }}>
          {formContent}
        </div>
      </div>
    </div>
  )
}
